/**
 * BB-MULTIPLAYER.JS - Multiplayer System Module
 * Real-time cursors and avatars via Liveblocks
 * With queue system for controlled batch releases
 * 
 * @version 2.0.0
 * @requires BB.Player for user identity
 */

window.BB = window.BB || {};

window.BB.Multiplayer = (function(Player) {
  'use strict';
  
  var VERSION = '2.0.0';
  var client, room;
  var cursorLayer, avatarLayer;
  var isActive = false;
  var isInitializing = false; // Prevent race conditions during async init
  var isIOSDisabled = false; // iOS Safari mode - multiplayer disabled but callbacks should fire
  var myStatus = 'loading'; // 'loading' | 'playing'
  var others = [];
  var myAvatarPosition = 0.5; // 0-1 position on progress bar
  var config = { sectionId: 'global' }; // Will be set on init
  
  // Render loop state
  var peerData = new Map(); // connectionId -> { x, y, avatarX, targetX, targetY, targetAvatarX, ... }
  var animationFrameId = null;
  var lastRenderTime = 0;
  var myProgressAvatar = null;
  
  // ========================================================================
  // PERFORMANCE OPTIMIZATIONS
  // ========================================================================
  var frameCount = 0;                    // For frame throttling
  var PEER_RENDER_INTERVAL = 2;          // Render peers every N frames (2 = 30fps)
  var MOVEMENT_THRESHOLD = 1;            // Min px change to trigger render
  var MAX_VISIBLE_PEERS = 12;            // Cap visible peer avatars/cursors
  var cachedWindowWidth = window.innerWidth;
  var cachedWindowHeight = window.innerHeight;
  
  // Apply low-perf CSS class to body when performance tier is low
  function applyPerfClass() {
    // Check after a small delay to ensure BBPerf is initialized
    setTimeout(function() {
      if (window.BBPerf && window.BBPerf.tier === 'low') {
        document.body.classList.add('bb-perf-low');
        console.log('⚡ [Multiplayer] Low-perf mode: Disabling glow/particle effects');
      } else if (window.BBPerf && window.BBPerf.enableParticles === false) {
        document.body.classList.add('bb-perf-low');
        console.log('⚡ [Multiplayer] Particles disabled: Applying low-perf CSS');
      }
    }, 100);
  }
  
  // Hide/show other users' avatars and cursors for performance
  var peersHidden = false;
  function setPeersVisible(visible) {
    peersHidden = !visible;
    
    // Hide all peer cursors and avatars via CSS class on body
    if (visible) {
      document.body.classList.remove('bb-hide-peers');
      console.log('👥 [Multiplayer] Peer avatars visible');
    } else {
      document.body.classList.add('bb-hide-peers');
      console.log('👥 [Multiplayer] Peer avatars hidden (performance mode)');
    }
  }
  
  // Update cached dimensions on resize (debounced)
  var resizeTimeout = null;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(function() {
      cachedWindowWidth = window.innerWidth;
      cachedWindowHeight = window.innerHeight;
    }, 100);
  });
  
  // Local avatar state for smooth rendering (updated via RAF, not mousemove)
  var myTargetAvatarX = 0.5;      // Target position (set by mousemove)
  var myCurrentAvatarX = 0.5;     // Interpolated position (rendered each frame)
  var avatarBarWidth = 300;       // Cached bar width (updated on resize)
  
  // Queue system state
  var FIREBASE_FUNCTIONS_URL = 'https://us-central1-pipdecksapp.cloudfunctions.net';
  var myBatchNumber = null;
  var myRoomId = null;
  var globalQueueCount = 0;
  var liveTotalWaiting = 0;  // LIVE count from Liveblocks (fetched from backend)
  var isReleased = false;
  var firebaseDb = null;
  var queueCallbacks = {
    onRelease: [],
    onQueueUpdate: []
  };
  
  // SIMPLIFIED: Single 10-second countdown before showing Enter button
  var ENTRY_COUNTDOWN_MS = 10 * 1000; // 10 seconds
  var entryCountdownEnd = null;
  var entryCountdownInterval = null;
  
  // Legacy variables (kept for compatibility but not used in simplified flow)
  var AUTO_RELEASE_COUNTDOWN_MS = 60 * 1000;
  var releaseCountdownEnd = null;
  var countdownInterval = null;
  var DOOR_AUTO_RELEASE_MS = 1 * 60 * 1000;
  var doorOpenCountdownEnd = null;
  var doorCountdownInterval = null;
  
  // Twemoji integration for consistent emoji rendering
  var twemojiLoaded = false;
  
  // Load Twemoji library
  function loadTwemoji() {
    if (twemojiLoaded || window.twemoji) {
      twemojiLoaded = true;
      return Promise.resolve();
    }
    
    return new Promise(function(resolve) {
      var script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/twemoji@14.0.2/dist/twemoji.min.js';
      script.onload = function() {
        twemojiLoaded = true;
        console.log('🎨 [BB.Multiplayer] Twemoji loaded');
        resolve();
      };
      script.onerror = function() {
        console.warn('⚠️ [BB.Multiplayer] Twemoji failed to load, using native emojis');
        resolve();
      };
      document.head.appendChild(script);
    });
  }
  
  // Parse emojis in an element using Twemoji
  function parseEmojis(element) {
    if (!element) return;
    if (window.twemoji) {
      window.twemoji.parse(element, {
        folder: 'svg',
        ext: '.svg',
        className: 'twemoji'
      });
    }
  }
  
  // Animal room names (mix of team animals: Cat, Rabbit, Frog, Chick)
  var ROOM_NAMES = [
    'Cheetah', 'Panther', 'Lynx', 'Tiger', 'Lion', 'Jaguar', 'Leopard', 'Cougar',  // Feline
    'Hare', 'Fox', 'Wolf', 'Bear', 'Otter', 'Badger', 'Stoat', 'Mink',              // Mammal  
    'Gecko', 'Newt', 'Toad', 'Axolotl', 'Salamander', 'Treefrog', 'Bullfrog',       // Amphibian
    'Eagle', 'Falcon', 'Hawk', 'Owl', 'Raven', 'Sparrow', 'Robin', 'Finch',         // Avian
    'Puma', 'Ocelot', 'Bobcat', 'Serval', 'Caracal', 'Fennec', 'Coyote', 'Dingo',   // More animals
    'Pelican', 'Heron', 'Crane', 'Stork', 'Flamingo', 'Swan', 'Goose', 'Duck'       // More birds
  ];
  
  function getRoomName(batchNumber) {
    if (!batchNumber || batchNumber < 1) return '—';
    var index = (batchNumber - 1) % ROOM_NAMES.length;
    return ROOM_NAMES[index];
  }
  
  // ========================================================================
  // INITIALIZATION
  // ========================================================================
  
  async function init(sectionConfig) {
    // #region agent log - H2 ESM import tracking
    if (window._bbDebugLog) window._bbDebugLog('bb-multiplayer.js:init', 'Multiplayer init started', { isActive: isActive, isInitializing: isInitializing }, 'H2');
    // #endregion
    
    // iOS Safari fix: Disable multiplayer entirely to prevent WKWebView memory crashes
    var isIOSSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent) && /iPhone|iPad/.test(navigator.userAgent);
    if (isIOSSafari) {
      console.log('📱 [BB.Multiplayer] Disabled on iOS Safari to prevent memory crashes');
      // Set flag so onRelease() calls trigger immediately
      isIOSDisabled = true;
      isInitializing = false;
      
      // Show the enter button directly (since updateEnterButton won't be called)
      // Wait for loading to complete, then show button (fast polling for instant response)
      var checkAndShowButton = function() {
        if (window.BB && window.BB.loadingComplete) {
          document.querySelectorAll('[id^="bb-queue-enter-"]').forEach(function(el) {
            el.classList.remove('bb-hidden');
            el.disabled = false;
            el.style.opacity = '1';
          });
          console.log('📱 [BB.Multiplayer] iOS: Enter button shown');
        } else {
          setTimeout(checkAndShowButton, 100); // Fast polling (100ms)
        }
      };
      checkAndShowButton(); // Start immediately, no initial delay
      
      return;
    }
    
    // Prevent multiple simultaneous init calls (race condition protection)
    if (isActive || isInitializing) {
      console.log('⚠️ [BB.Multiplayer] Init already running or complete, skipping');
      return;
    }
    isInitializing = true;
    
    if (sectionConfig) {
      config = sectionConfig;
    }
    
    // PERFORMANCE: Apply low-perf CSS class to disable expensive effects
    applyPerfClass();
    
    // Start session tracking for analytics
    startSessionTracking();
    
    // Track unique visitor (once per day)
    trackVisitor();
    
    // Load Twemoji for consistent emoji rendering across platforms
    // Await it to ensure emojis render correctly on first paint
    loadTwemoji().then(function() {
      console.log('🎨 [BB.Multiplayer] Twemoji ready for avatar rendering');
    });
    
    // Set up cursor layer and events IMMEDIATELY (before Liveblocks connects)
    setupCursorLayer();
    // Avatar layer is created lazily in renderFrame when progress bar exists
    bindEvents();
    
    // Start render loop
    startRenderLoop();
    
    // Initialize Firebase for queue tracking
    await initFirebase();
    
    // Join the queue and get batch assignment
    var queueResult = await joinQueue();
    if (queueResult) {
      myBatchNumber = queueResult.batchNumber;
      myRoomId = queueResult.roomId;
      console.log('🎟️ [BB.Multiplayer] Assigned to batch', myBatchNumber, 'room:', myRoomId);
      
      myStatus = 'loading';
      
      // SIMPLIFIED: 
      // - If RECONNECTING (refresh/return) → show button immediately
      // - If NEW to room → 30-second countdown, then show button
      if (queueResult.reconnect) {
        console.log('🔄 [BB.Multiplayer] Reconnecting to existing room - skipping countdown');
        isReleased = true; // Show button immediately
      } else {
        console.log('🆕 [BB.Multiplayer] New room assignment - starting 10s countdown');
        isReleased = false;
        startSimpleEntryCountdown();
      }
      
      // Update UI immediately
      updateStatusCount();
      
      // Listen for loading complete to update button visibility
      if (window.BB && window.BB.onLoadingComplete) {
        window.BB.onLoadingComplete(function() {
          console.log('🔄 [BB.Multiplayer] Loading complete - updating enter button');
          updateStatusCount();
        });
      }
    }
    
    try {
      // #region agent log - H2 ESM import attempt
      if (window._bbDebugLog) window._bbDebugLog('bb-multiplayer.js:240', 'Starting Liveblocks ESM import', {}, 'H2');
      // #endregion
      
      // Dynamic import for ESM module
      var { createClient } = await import('https://esm.sh/@liveblocks/client@1.9.1');
      
      // #region agent log - H2 ESM import success
      if (window._bbDebugLog) window._bbDebugLog('bb-multiplayer.js:245', 'Liveblocks ESM import SUCCESS', { hasCreateClient: typeof createClient === 'function' }, 'H2');
      // #endregion
      
      // Use auth endpoint instead of public API key for better control
      client = createClient({
        authEndpoint: async (roomArg) => {
          var playerName = Player ? Player.getName() : 'Anonymous';
          var playerColor = Player ? Player.getColor() : { bg: '#fe5f55', text: '#ffffff' };
          var playerSprite = Player ? Player.getSprite() : { emoji: '🃏' };
          
          var response = await fetch(FIREBASE_FUNCTIONS_URL + '/liveblocksAuth', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              room: roomArg,
              odId: Player ? Player.getName() : 'anon_' + Math.random().toString(36).substr(2, 9),
              name: playerName,
              color: playerColor.bg,
              sprite: playerSprite.emoji
            })
          });
          return response.json();
        }
      });
      
      // Use assigned room from queue, or fallback to default
      var roomId = myRoomId || ("bb-wait-" + (config.sectionId || "global"));
      
      var playerName = Player ? Player.getName() : 'Anonymous';
      var playerColor = Player ? Player.getColor() : { bg: '#fe5f55', text: '#ffffff' };
      var playerSprite = Player ? Player.getSprite() : { emoji: '🃏' };
      var playerChain = Player ? Player.getSelectedChain() : null;
      var teamEmoji = playerChain ? playerChain.baseEmoji : '🃏';
      
      var { room: joinedRoom, leave } = client.enterRoom(roomId, {
        initialPresence: {
          cursor: null,
          status: 'loading',
          name: playerName,
          color: playerColor,
          sprite: playerSprite,
          teamEmoji: teamEmoji,
          avatarX: 0.5,  // Position on progress bar (0-1)
          savings: 0,
          batchNumber: myBatchNumber
        }
      });
      
      room = joinedRoom;
      isActive = true;
      
      console.log('🟢 [BB.Multiplayer] Connected to room', roomId);
      console.log('🎭 Player:', playerName);
      
      // Subscribe to others - JUST UPDATE DATA, DON'T RENDER DIRECTLY
      room.subscribe("others", function(newOthers) {
        others = newOthers;
        updateStatusCount();
        
        // Sync peerData map with current others list
        // 1. Add/Update peers
        others.forEach(function(user) {
          if (!user.presence) return;
          
          var peer = peerData.get(user.connectionId);
          if (!peer) {
            peer = { 
              x: user.presence.cursor ? user.presence.cursor.x : 0, 
              y: user.presence.cursor ? user.presence.cursor.y : 0,
              avatarX: user.presence.avatarX || 0.5,
              currentX: user.presence.cursor ? user.presence.cursor.x : 0,
              currentY: user.presence.cursor ? user.presence.cursor.y : 0,
              currentAvatarX: user.presence.avatarX || 0.5,
              isFlying: user.presence.isFlying || false,
              flyX: user.presence.flyX || 0,
              flyY: user.presence.flyY || 0,
              currentFlyX: user.presence.flyX || 0,
              currentFlyY: user.presence.flyY || 0
            };
            peerData.set(user.connectionId, peer);
          }
          
          // Update targets
          if (user.presence.cursor) {
            peer.x = user.presence.cursor.x;
            peer.y = user.presence.cursor.y;
          }
          if (typeof user.presence.avatarX === 'number') {
            peer.avatarX = user.presence.avatarX;
          }
          
          // Sync fly data
          peer.isFlying = user.presence.isFlying;
          peer.flyX = user.presence.flyX;
          peer.flyY = user.presence.flyY;
          
          // Store presence data for rendering
          peer.presence = user.presence;
        });
        
        // 2. Remove stale peers
        var activeIds = new Set(others.map(function(u) { return u.connectionId; }));
        peerData.forEach(function(peer, id) {
          if (!activeIds.has(id)) {
            peerData.delete(id);
            // Cleanup DOM elements immediately using cached refs
            if (peer.cursorEl) {
              peer.cursorEl.remove();
            } else {
              var cursorEl = document.getElementById('cursor-' + id);
              if (cursorEl) cursorEl.remove();
            }
            
            if (peer.avatarEl) {
              peer.avatarEl.remove();
            } else {
              var avatarEl = document.getElementById('avatar-' + id);
              if (avatarEl) avatarEl.remove();
            }
          }
        });
        
        // Update fly game leaderboard if active
        if (flyGameActive) {
          updateFlyLeaderboard();
        }
      });
      
      // Listen for broadcast events (Fly-off, Jumps)
      room.subscribe("event", function(eventData) {
        var event = eventData.event;
        var connectionId = eventData.connectionId;
        
        if (event.type === 'PLAYER_FLY_OFF') {
          createFlyOffAnimation(event.name, event.color, event.sprite, event.savings, event.avatarX * window.innerWidth);
        }
        else if (event.type === 'PLAYER_JUMP') {
          // Trigger jump for a remote user
          var targetId = connectionId || event.connectionId;
          
          var avatarEl = document.getElementById('avatar-' + targetId);
          if (avatarEl) {
            var spriteEl = avatarEl.querySelector('.bb-mp-avatar-sprite');
            if (spriteEl) {
              // Reset animation
              spriteEl.style.animation = 'none';
              void spriteEl.offsetWidth; // Force reflow
              spriteEl.style.animation = '';
              
              // Add jump class to parent
              avatarEl.classList.add('bb-avatar-jump');
              setTimeout(function() {
                avatarEl.classList.remove('bb-avatar-jump');
              }, 500);
            }
            
            // Show product message if provided
            if (event.productName) {
              var msgColor = event.color || { bg: '#fe5f55', text: '#ffffff' };
              showAvatarMessage(avatarEl, event.productName, msgColor);
            }
          }
        }
        else if (event.type === 'PLAYER_EVOLVE') {
          // Trigger evolution animation for a remote user
          var targetId = connectionId || event.connectionId;
          var avatarEl = document.getElementById('avatar-' + targetId);
          
          if (avatarEl) {
            var spriteEl = avatarEl.querySelector('.bb-mp-avatar-sprite');
            
            // Add white flash BEFORE changing the sprite (Mario-style)
            avatarEl.classList.remove('bb-avatar-evolve', 'bb-avatar-devolve', 'bb-avatar-legendary');
            void avatarEl.offsetWidth;
            
            if (event.isFinalForm) {
              avatarEl.classList.add('bb-avatar-legendary');
              console.log('🐉 Someone became LEGENDARY!');
            } else if (event.evolved) {
              avatarEl.classList.add('bb-avatar-evolve');
            } else if (event.devolved) {
              avatarEl.classList.add('bb-avatar-devolve');
            }
            
            // Update sprite AFTER flash starts
            if (spriteEl && event.tierSprite) {
              setTimeout(function() {
                spriteEl.textContent = event.tierSprite.emoji;
                spriteEl.style.transform = 'scale(' + event.tierSprite.scale + ')';
                spriteEl.style.setProperty('--avatar-scale', event.tierSprite.scale);
              }, 50);
            }
            
            setTimeout(function() {
              avatarEl.classList.remove('bb-avatar-evolve', 'bb-avatar-devolve', 'bb-avatar-legendary');
            }, 2500);
          }
        }
        // Handle batch release event from admin (or auto-release)
        else if (event.type === 'BATCH_RELEASED') {
          console.log('🎉 [BB.Multiplayer] Batch released!', event.batchNumber, event.autoReleased ? '(AUTO)' : '');
          handleBatchRelease(event.batchNumber, event.autoReleased);
        }
        // FLY GAME: Hoop spawned by another player
        else if (event.type === 'HOOP_SPAWN') {
          if (flyGameActive && !currentHoop) {
            console.log('🎯 [FLY GAME] Hoop spawned by another player');
            spawnHoop(true, {
              x: event.x,
              y: event.y,
              spawnedBy: event.spawnedBy,
              id: event.id
            });
          }
        }
        // FLY GAME: Another player collected the hoop
        else if (event.type === 'HOOP_COLLECTED') {
          if (currentHoop && currentHoop.id === event.hoopId && !currentHoop.collected) {
            console.log('🎯 [FLY GAME]', event.collectorName, 'scored!');
            collectHoop(event.collectorId, event.collectorName, event.collectorSprite, event.collectorColor);
            updateFlyLeaderboard();
          }
        }
        // FLY GAME: Got shunted by another player
        else if (event.type === 'PLAYER_SHUNT') {
          if (flyModeActive && room && event.toId === room.getSelf().connectionId) {
            // Apply the shunt force
            flyVelocity.x += event.forceX;
            flyVelocity.y += event.forceY;
            
            // Visual feedback
            if (myProgressAvatar) {
              myProgressAvatar.classList.add('bb-fly-shunt');
              setTimeout(function() {
                if (myProgressAvatar) myProgressAvatar.classList.remove('bb-fly-shunt');
              }, 200);
            }
            
            console.log('💥 [FLY GAME] Got shunted!');
          }
        }
      });
      
    } catch (e) {
      // #region agent log - H2/H5 Multiplayer init failure
      if (window._bbDebugLog) window._bbDebugLog('bb-multiplayer.js:catch', 'Multiplayer init FAILED', { error: String(e).substring(0, 200), stack: e.stack ? e.stack.substring(0, 300) : 'no stack' }, 'H2');
      // #endregion
      console.warn('🔴 [BB.Multiplayer] Failed to initialize', e);
    }
  }
  
  // ========================================================================
  // FIREBASE & QUEUE SYSTEM
  // ========================================================================
  
  async function initFirebase() {
    try {
      // Dynamic import Firebase - may fail if blocked by content blocker
      var firebase = await import('https://www.gstatic.com/firebasejs/10.7.1/firebase-app.js');
      var firebaseDatabase = await import('https://www.gstatic.com/firebasejs/10.7.1/firebase-database.js');
      
      var firebaseConfig = {
        apiKey: "AIzaSyBXqjZCjJ9vLj1L_mpjZ6_G8BMcKl9PMys",
        authDomain: "pipdecksapp.firebaseapp.com",
        databaseURL: "https://pipdecksapp-default-rtdb.firebaseio.com",
        projectId: "pipdecksapp",
        storageBucket: "pipdecksapp.appspot.com",
        messagingSenderId: "547280603498",
        appId: "1:547280603498:web:8b8b8b8b8b8b8b8b8b8b8b"
      };
      
      var app = firebase.initializeApp(firebaseConfig, 'bb-multiplayer');
      firebaseDb = firebaseDatabase.getDatabase(app);
      
      // Listen to global queue count
      var queueRef = firebaseDatabase.ref(firebaseDb, 'queue/total');
      firebaseDatabase.onValue(queueRef, function(snapshot) {
        globalQueueCount = snapshot.val() || 0;
        updateQueueDisplay();
        triggerQueueCallbacks('onQueueUpdate', globalQueueCount);
      });
      
      console.log('🔥 [BB.Multiplayer] Firebase initialized for queue tracking');
      
    } catch (e) {
      // Firebase blocked by content blocker - this is OK, system will run in open mode
      console.warn('🔴 [BB.Multiplayer] Firebase init failed (likely blocked) - running in open mode');
      firebaseDb = null; // Ensure it's null so checkDoorState defaults to open
    }
  }
  
  async function joinQueue() {
    try {
      var playerName = Player ? Player.getName() : 'Anonymous';
      var playerColor = Player ? Player.getColor() : { bg: '#fe5f55', text: '#ffffff' };
      var playerSprite = Player ? Player.getSprite() : { emoji: '🃏' };
      var team = playerSprite.chainId || 'feline';
      
      var response = await fetch(FIREBASE_FUNCTIONS_URL + '/joinQueue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          odId: playerName,
          name: playerName,
          team: team,
          color: playerColor.bg,
          sprite: playerSprite.emoji
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to join queue: ' + response.status);
      }
      
      var result = await response.json();
      
      if (result.reconnect) {
        console.log('🔄 [BB.Multiplayer] Reconnected to batch', result.batchNumber);
      }
      
      return result;
      
    } catch (e) {
      console.warn('🔴 [BB.Multiplayer] Failed to join queue:', e);
      // Fallback: return default batch
      return {
        batchNumber: 1,
        roomId: 'bb-wait-1',
        reconnect: false,
        position: 0
      };
    }
  }
  
  // Check current door state from Firebase (sync check on init)
  // IMPORTANT: Returns TRUE (open) when Firebase is unavailable (content blocked)
  // This ensures users can always enter the experience even with ad blockers
  // Returns: { open: boolean, autoReleased: boolean, releasedAt: number|null }
  async function checkDoorState(batchNumber) {
    // If Firebase failed to initialize (likely blocked by content blocker), default to OPEN
    if (!firebaseDb) {
      console.log('🚪 [BB.Multiplayer] Firebase unavailable - defaulting to OPEN mode');
      return { open: true, autoReleased: false, releasedAt: null, createdAt: null };
    }
    
    try {
      var firebaseDatabase = await import('https://www.gstatic.com/firebasejs/10.7.1/firebase-database.js');
      var batchRef = firebaseDatabase.ref(firebaseDb, 'batches/' + batchNumber);
      var snapshot = await firebaseDatabase.get(batchRef);
      var batch = snapshot.val();
      
      // GHOST ROOM FIX: If batch doesn't exist, treat as OPEN
      // This handles the case where a room was deleted after user was assigned
      if (!batch) {
        console.warn('👻 [BB.Multiplayer] Batch', batchNumber, 'does not exist (ghost room) - defaulting to OPEN');
        return { open: true, autoReleased: false, releasedAt: null, createdAt: null, ghostRoom: true };
      }
      
      var status = batch.status;
      var autoReleased = batch.autoReleased === true;
      var releasedAt = batch.releasedAt || null;
      var createdAt = batch.createdAt || null;
      console.log('🚪 [BB.Multiplayer] Door state for batch', batchNumber, ':', status, autoReleased ? '(AUTO)' : '', releasedAt ? 'at ' + new Date(releasedAt).toLocaleTimeString() : '', createdAt ? 'created ' + new Date(createdAt).toLocaleTimeString() : '');
      return { open: status === 'released', autoReleased: autoReleased, releasedAt: releasedAt, createdAt: createdAt };
    } catch (e) {
      // If request blocked or failed, default to OPEN to not block users
      console.warn('🚪 [BB.Multiplayer] Door state check failed - defaulting to OPEN:', e.message);
      return { open: true, autoReleased: false, releasedAt: null, createdAt: null };
    }
  }
  
  function listenForBatchRelease(batchNumber) {
    if (!firebaseDb) return;
    
    import('https://www.gstatic.com/firebasejs/10.7.1/firebase-database.js').then(function(firebaseDatabase) {
      var batchRef = firebaseDatabase.ref(firebaseDb, 'batches/' + batchNumber + '/status');
      
      firebaseDatabase.onValue(batchRef, function(snapshot) {
        var status = snapshot.val();
        if (status === 'released' && !isReleased) {
          console.log('🎉 [BB.Multiplayer] Your batch has been released!');
          handleBatchRelease(batchNumber);
        } else if (status === 'waiting' && isReleased) {
          // Door was CLOSED after being open (admin action)
          console.log('🔒 [BB.Multiplayer] Door was closed by admin');
          isReleased = false;
          myStatus = 'loading';
          updateStatusCount();
        }
      });
    });
  }
  
  function handleBatchRelease(batchNumber, autoReleased) {
    if (isReleased) return;
    if (batchNumber !== myBatchNumber) return;
    
    console.log('🚪 [BB.Multiplayer] Door OPENED' + (autoReleased ? ' (AUTO)' : '') + ' - updating UI');
    isReleased = true;
    
    // Clear door waiting countdown (door is now open!)
    if (doorCountdownInterval) {
      clearInterval(doorCountdownInterval);
      doorCountdownInterval = null;
    }
    doorOpenCountdownEnd = null;
    
    // Stop door polling if active
    stopDoorPoll();
    
    // If auto-released, start a 1-minute countdown before showing enter button
    if (autoReleased) {
      releaseCountdownEnd = Date.now() + AUTO_RELEASE_COUNTDOWN_MS;
      console.log('⏱️ [BB.Multiplayer] Auto-release countdown started (60 seconds)');
      startCountdownTimer();
    }
    
    // UPDATE UI IMMEDIATELY
    updateStatusCount();
    
    // Trigger callbacks
    triggerQueueCallbacks('onRelease', batchNumber);
    
    // Visual feedback
    var statusEl = document.getElementById('bb-mp-position');
    if (statusEl) {
      if (autoReleased && releaseCountdownEnd) {
        // Show countdown message
        statusEl.innerHTML = '🎉 <strong>Doors opening!</strong> Get ready...';
      } else {
        statusEl.innerHTML = '🎉 <strong>You\'re in!</strong> Get ready...';
      }
    }
  }
  
  // Start countdown timer for auto-release
  function startCountdownTimer() {
    if (countdownInterval) clearInterval(countdownInterval);
    
    countdownInterval = setInterval(function() {
      updateStatusCount(); // This will update the countdown display
      
      // Check if countdown is complete
      if (!releaseCountdownEnd || Date.now() >= releaseCountdownEnd) {
        clearInterval(countdownInterval);
        countdownInterval = null;
        releaseCountdownEnd = null;
        console.log('✅ [BB.Multiplayer] Auto-release countdown complete');
        updateStatusCount(); // Final update to show enter button
      }
    }, 1000);
  }
  
  // Check if countdown is active (button should be hidden)
  function isCountdownActive() {
    return releaseCountdownEnd && Date.now() < releaseCountdownEnd;
  }
  
  // Get remaining countdown seconds
  function getCountdownRemaining() {
    if (!releaseCountdownEnd) return 0;
    return Math.max(0, Math.ceil((releaseCountdownEnd - Date.now()) / 1000));
  }
  
  // Start countdown timer for waiting room (until door auto-opens)
  function startDoorCountdownTimer() {
    if (doorCountdownInterval) clearInterval(doorCountdownInterval);
    
    doorCountdownInterval = setInterval(function() {
      updateStatusCount(); // This will update the countdown display
      
      // Check if countdown is complete (door should open soon)
      if (!doorOpenCountdownEnd || Date.now() >= doorOpenCountdownEnd) {
        clearInterval(doorCountdownInterval);
        doorCountdownInterval = null;
        doorOpenCountdownEnd = null;
        console.log('⏱️ [BB.Multiplayer] Door auto-open countdown complete - starting door poll...');
        // Start polling for door to open (server runs every 1 min, may be delayed)
        startDoorPoll();
        updateStatusCount();
      }
    }, 1000);
  }
  
  // ========================================================================
  // SIMPLIFIED ENTRY COUNTDOWN
  // Just wait 30 seconds, then show the Enter button. No complexity.
  // ========================================================================
  
  function startSimpleEntryCountdown() {
    // SKIP COUNTDOWN - immediately release
    // User requested no countdown timer in waiting area
    if (entryCountdownInterval) clearInterval(entryCountdownInterval);
    entryCountdownInterval = null;
    entryCountdownEnd = null;
    
    console.log('✅ [BB.Multiplayer] Skipping countdown - immediately ready');
    isReleased = true;
    updateStatusCount();
    return;
    
    /* ORIGINAL COUNTDOWN CODE (disabled)
    entryCountdownEnd = Date.now() + ENTRY_COUNTDOWN_MS;
    console.log('⏱️ [BB.Multiplayer] Starting 30-second entry countdown');
    
    entryCountdownInterval = setInterval(function() {
      var remaining = Math.max(0, entryCountdownEnd - Date.now());
      var secs = Math.ceil(remaining / 1000);
      
      // Update the countdown display
      updateSimpleCountdownDisplay(secs);
      
      if (remaining <= 0) {
        // Countdown complete - show the Enter button!
        clearInterval(entryCountdownInterval);
        entryCountdownInterval = null;
        entryCountdownEnd = null;
        
        console.log('✅ [BB.Multiplayer] Entry countdown complete - showing Enter button');
        isReleased = true;
        updateStatusCount();
      }
    }, 1000);
    */
    
    // Show initial countdown immediately (disabled)
    updateSimpleCountdownDisplay(Math.ceil(ENTRY_COUNTDOWN_MS / 1000));
  }
  
  function updateSimpleCountdownDisplay(secs) {
    // CONSOLIDATED: All DOM updates go through updateStatusCount() to prevent
    // race conditions where multiple functions fight over the same element.
    // updateStatusCount() checks isSimpleCountdownActive() and displays the
    // correct content (countdown timer OR "Ready to enter!" message).
    updateStatusCount();
  }
  
  function getSimpleCountdownRemaining() {
    if (!entryCountdownEnd) return 0;
    return Math.max(0, Math.ceil((entryCountdownEnd - Date.now()) / 1000));
  }
  
  function isSimpleCountdownActive() {
    return entryCountdownEnd && Date.now() < entryCountdownEnd;
  }
  
  // ========================================================================
  // LEGACY: Door polling (kept but not used in simplified flow)
  // ========================================================================
  
  // Poll for door state when countdown passes but door hasn't opened yet
  // This handles the case where server auto-release is delayed (runs every 1 min)
  var doorPollInterval = null;
  var doorPollAttempts = 0;
  var DOOR_POLL_INTERVAL_MS = 2000; // Poll every 2 seconds (aggressive)
  var MAX_DOOR_POLL_ATTEMPTS = 15; // 15 attempts * 2 seconds = 30 seconds max before force release
  
  function startDoorPoll() {
    if (doorPollInterval) clearInterval(doorPollInterval);
    if (isReleased) {
      console.log('🔄 [BB.Multiplayer] Skipping door poll - already released');
      return;
    }
    
    doorPollAttempts = 0;
    console.log('🔄 [BB.Multiplayer] Starting door poll (every 3 seconds, max 2 minutes)');
    
    // Poll immediately once
    pollDoorState();
    
    // Then poll every 3 seconds
    doorPollInterval = setInterval(function() {
      doorPollAttempts++;
      
      if (isReleased) {
        // Door opened! Stop polling
        clearInterval(doorPollInterval);
        doorPollInterval = null;
        console.log('✅ [BB.Multiplayer] Door poll succeeded - door is open');
        return;
      }
      
      // Log progress every 10 attempts (30 seconds)
      if (doorPollAttempts % 10 === 0) {
        console.log('🔄 [BB.Multiplayer] Still polling... attempt', doorPollAttempts, 'of', MAX_DOOR_POLL_ATTEMPTS);
      }
      
      if (doorPollAttempts >= MAX_DOOR_POLL_ATTEMPTS) {
        // Give up after too many attempts - something is wrong with server
        clearInterval(doorPollInterval);
        doorPollInterval = null;
        console.error('❌ [BB.Multiplayer] Door poll gave up after', MAX_DOOR_POLL_ATTEMPTS, 'attempts (' + (MAX_DOOR_POLL_ATTEMPTS * DOOR_POLL_INTERVAL_MS / 1000) + ' seconds)');
        // Force release as last resort so user isn't stuck
        console.log('🔓 [BB.Multiplayer] FORCING release to prevent user from being stuck');
        forceRelease();
        updateStatusCount();
        return;
      }
      
      pollDoorState();
    }, DOOR_POLL_INTERVAL_MS);
  }
  
  var consecutivePollFailures = 0;
  var MAX_CONSECUTIVE_FAILURES = 5;
  
  async function pollDoorState() {
    try {
      var doorState = await checkDoorState(myBatchNumber);
      consecutivePollFailures = 0; // Reset on success
      
      console.log('🔄 [BB.Multiplayer] Poll attempt', doorPollAttempts + 1, '- door open:', doorState.open, doorState.ghostRoom ? '(GHOST ROOM)' : '');
      
      if (doorState.open) {
        // Door is now open (or ghost room - let them in either way)
        if (doorPollInterval) {
          clearInterval(doorPollInterval);
          doorPollInterval = null;
        }
        
        if (doorState.ghostRoom) {
          console.log('👻 [BB.Multiplayer] Ghost room detected during poll - letting user in immediately');
        }
        
        handleBatchRelease(myBatchNumber, doorState.autoReleased);
      }
    } catch (e) {
      consecutivePollFailures++;
      console.warn('🔄 [BB.Multiplayer] Door poll failed (attempt', consecutivePollFailures, '):', e.message);
      
      // If we fail too many times in a row, force release
      if (consecutivePollFailures >= MAX_CONSECUTIVE_FAILURES) {
        console.error('❌ [BB.Multiplayer]', MAX_CONSECUTIVE_FAILURES, 'consecutive poll failures - forcing release');
        if (doorPollInterval) {
          clearInterval(doorPollInterval);
          doorPollInterval = null;
        }
        forceRelease();
        updateStatusCount();
      }
    }
  }
  
  function stopDoorPoll() {
    if (doorPollInterval) {
      clearInterval(doorPollInterval);
      doorPollInterval = null;
    }
  }
  
  // Check if door countdown is active (waiting for door to open)
  function isDoorCountdownActive() {
    return doorOpenCountdownEnd && Date.now() < doorOpenCountdownEnd;
  }
  
  // Get remaining door countdown seconds
  function getDoorCountdownRemaining() {
    if (!doorOpenCountdownEnd) return 0;
    return Math.max(0, Math.ceil((doorOpenCountdownEnd - Date.now()) / 1000));
  }
  
  function triggerQueueCallbacks(type, data) {
    if (queueCallbacks[type]) {
      queueCallbacks[type].forEach(function(cb) {
        try { cb(data); } catch(e) { console.error(e); }
      });
    }
  }
  
  function onRelease(callback) {
    queueCallbacks.onRelease.push(callback);
    // On iOS Safari, multiplayer is disabled - fire callback immediately
    if (isIOSDisabled || isReleased) callback(myBatchNumber);
  }
  
  function onQueueUpdate(callback) {
    queueCallbacks.onQueueUpdate.push(callback);
    callback(globalQueueCount);
  }
  
  function updateQueueDisplay() {
    // Get LIVE count from Liveblocks presence (deduplicated by userId)
    // Same user with multiple tabs = 1 user
    var seenUserIds = new Set();
    var uniqueOthersCount = 0;
    others.forEach(function(u) {
      var userId = (u.info && u.info.name) || u.connectionId;
      if (!seenUserIds.has(userId)) {
        seenUserIds.add(userId);
        uniqueOthersCount++;
      }
    });
    var liveRoomCount = uniqueOthersCount + 1;
    
    // Update room count elements with LIVE data
    var roomUsersEls = document.querySelectorAll('[id^="bb-room-users-"]');
    roomUsersEls.forEach(function(el) {
      el.textContent = liveRoomCount;
    });
    
    // For total waiting, we need to fetch from backend or estimate
    // For now, show live room count * estimated rooms (or fetch periodically)
    var totalWaitingEls = document.querySelectorAll('[id^="bb-total-waiting-"]');
    totalWaitingEls.forEach(function(el) {
      // Use cached live total if we have it, otherwise show room count
      el.textContent = liveTotalWaiting || liveRoomCount;
    });
    
    // Legacy support
    var totalEl = document.getElementById('bb-queue-total');
    if (totalEl) {
      totalEl.textContent = liveTotalWaiting || liveRoomCount;
    }
    
    var batchEl = document.getElementById('bb-queue-batch');
    if (batchEl && myBatchNumber) {
      batchEl.textContent = getRoomName(myBatchNumber);
    }
  }
  
  // Fetch live total waiting from backend periodically
  function fetchLiveTotals() {
    fetch(FIREBASE_FUNCTIONS_URL + '/adminGetStatus?adminSecret=frodge')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.queue && typeof data.queue.liveWaiting === 'number') {
          liveTotalWaiting = data.queue.liveWaiting;
          updateQueueDisplay();
        }
      })
      .catch(function(e) {
        // Silent fail - use room count as fallback
      });
  }
  
  // Fetch live totals every 5 seconds while waiting
  setInterval(function() {
    if (!isReleased && myBatchNumber) {
      fetchLiveTotals();
    }
  }, 5000);
  
  function getGlobalQueueCount() {
    return globalQueueCount;
  }
  
  function getBatchNumber() {
    return myBatchNumber;
  }
  
  function getIsReleased() {
    return isReleased;
  }
  
  // DEV: Force release for bypass/testing
  function forceRelease() {
    console.log('🔓 [BB.Multiplayer] Force release triggered (DEV BYPASS)');
    isReleased = true;
    triggerQueueCallbacks('onRelease', myBatchNumber);
  }
  
  // ========================================================================
  // CURSOR LAYER SETUP
  // ========================================================================
  
  var myCursorEl = null;
  var myAvatarPreview = null;
  
  function setupCursorLayer() {
    // Create cursor layer for OTHER users' cursors
    if (!cursorLayer) {
      cursorLayer = document.createElement('div');
      cursorLayer.className = 'bb-cursor-layer';
      cursorLayer.id = 'bb-cursor-layer';
      document.body.appendChild(cursorLayer);
      console.log('🎯 Cursor layer created for others');
    }
    
    // Apply custom cursor and create avatar preview
    applyCustomCursor();
    
    console.log('🎯 Using native CSS cursor for self (zero lag)');
    
    // Status counter - shows queue position
    var intro = document.getElementById('bb-intro-' + config.sectionId);
    if (intro && !document.getElementById('bb-mp-status')) {
      var statusEl = document.createElement('div');
      statusEl.className = 'bb-mp-status';
      statusEl.id = 'bb-mp-status';
      statusEl.innerHTML = '<div class="bb-mp-dot"></div> <span id="bb-mp-position">0 people ahead of you</span>';
      intro.appendChild(statusEl);
    }
  }
  
  function applyCustomCursor() {
    var playerColor = Player ? Player.getColor() : { bg: '#fe5f55', text: '#ffffff' };
    var playerSprite = Player ? Player.getSprite() : { emoji: '🃏' };
    var playerName = Player ? Player.getName() : 'Player';
    
    // Create SVG cursor with the player's color (native performance!)
    var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">' +
      '<path d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19841L11.7841 12.3673H5.65376Z" ' +
      'fill="' + playerColor.bg + '" stroke="white" stroke-width="1"/>' +
      '</svg>';
    
    // Encode as data URI
    var encoded = 'data:image/svg+xml,' + encodeURIComponent(svg);
    
    // Apply to intro overlay (and body as fallback)
    var intro = document.getElementById('bb-intro-' + config.sectionId);
    var cursorCSS = 'url("' + encoded + '") 0 0, auto';
    
    if (intro) {
      intro.style.cursor = cursorCSS;
    }
    document.body.style.cursor = cursorCSS;
    
    // Also create a style element for hover states
    var styleEl = document.getElementById('bb-custom-cursor-style');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'bb-custom-cursor-style';
      document.head.appendChild(styleEl);
    }
    // Create custom pointer cursor (pointing finger) for interactive elements
    // Simple design: thick index finger pointing up, wider rounded palm below
    var pointerSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">' +
      '<path d="M13 1c-1.1 0-2 .9-2 2v8H8c-1.66 0-3 1.34-3 3v3.5C5 20.09 7.24 22 10 22h5c2.76 0 5-1.91 5-4.5V14c0-1.66-1.34-3-3-3h-2V3c0-1.1-.9-2-2-2z" ' +
      'fill="' + playerColor.bg + '" stroke="white" stroke-width="0.75"/>' +
      '</svg>';
    var pointerEncoded = 'data:image/svg+xml,' + encodeURIComponent(pointerSvg);
    
    // Set custom cursor for intro overlay and custom pointer for interactive elements
    styleEl.textContent = 
      '.bb-intro-overlay { cursor: url("' + encoded + '") 0 0, auto; }' +
      '.bb-intro-overlay button, .bb-intro-overlay a, .bb-intro-overlay [role="button"], ' +
      '.bb-intro-overlay .bb-queue-card-container, .bb-intro-overlay .bb-my-avatar-sprite, ' +
      '.bb-intro-overlay .bb-my-avatar-name, .bb-intro-overlay .bb-queue-enter { cursor: url("' + pointerEncoded + '") 6 0, pointer !important; }';
    
    // Create/update avatar preview badge (interactive - click to customize!)
    if (!myAvatarPreview) {
      myAvatarPreview = document.createElement('div');
      myAvatarPreview.className = 'bb-my-avatar-preview';
      myAvatarPreview.id = 'bb-my-avatar-preview';
      
      // Insert into the avatar preview slot in the ready state container
      var avatarSlot = document.querySelector('[id^="bb-avatar-preview-slot-"]');
      if (avatarSlot) {
        avatarSlot.appendChild(myAvatarPreview);
      } else {
        // Fallback: try room info, then queue content
        var roomInfo = document.querySelector('.bb-queue-room-info');
        if (roomInfo && roomInfo.parentNode) {
          roomInfo.parentNode.insertBefore(myAvatarPreview, roomInfo.nextSibling);
        } else {
          var queueContent = document.querySelector('.bb-queue-content');
          if (queueContent) {
            queueContent.appendChild(myAvatarPreview);
          } else {
            document.body.appendChild(myAvatarPreview);
          }
        }
      }
      
      // Make it interactive
      myAvatarPreview.addEventListener('click', function(e) {
        var target = e.target;
        
        // Click on sprite to change team/avatar (also generates new team-appropriate name)
        // But only if team is not locked (via URL parameter)
        if (target.classList.contains('bb-my-avatar-sprite')) {
          if (Player && Player.isTeamLocked && Player.isTeamLocked()) {
            console.log('[BB.Multiplayer] Team is locked (assigned via URL)');
            // Add a subtle shake animation to indicate locked
            target.style.animation = 'none';
            target.offsetHeight; // Trigger reflow
            target.style.animation = 'bb-shake 0.4s ease';
            return;
          }
          if (Player) Player.cycleSprite();
          updateMyCursor();
          syncQueueAvatars(); // Update self avatar in queue
          if (room) {
            room.updatePresence({
              sprite: Player ? Player.getSprite() : { emoji: '🃏' },
              name: Player ? Player.getName() : 'Player'
            });
          }
        }
        
        // Click on name to randomize it
        if (target.classList.contains('bb-my-avatar-name')) {
          if (Player) Player.generateNewName();
          updateMyCursor();
          if (room) {
            room.updatePresence({
              name: Player ? Player.getName() : 'Player'
            });
          }
        }
      });
    }
    
    // Get the queue avatars and count text to move inside
    var queueAvatarsContainer = document.querySelector('[id^="bb-queue-avatars-"]');
    var queueCountText = document.querySelector('[id^="bb-queue-count-text-"]');
    
    var isLocked = Player && Player.isTeamLocked && Player.isTeamLocked();
    var spriteTitle = isLocked ? 'Team locked (joined via invite link)' : 'Click to change team';
    var lockIcon = isLocked ? ' 🔒' : '';
    
    myAvatarPreview.innerHTML = 
      '<div class="bb-my-avatar-top">' +
        '<div class="bb-my-avatar-sprite' + (isLocked ? ' bb-team-locked' : '') + '" title="' + spriteTitle + '">' + playerSprite.emoji + '</div>' +
        '<div class="bb-my-avatar-info">' +
          '<div class="bb-my-avatar-name" title="Click to randomize name" style="cursor:pointer">' + playerName + '</div>' +
          '<div class="bb-my-avatar-row">' +
            '<div class="bb-my-avatar-label">YOU ARE ON TEAM ' + (playerSprite.name || 'PLAYER').toUpperCase() + lockIcon + '</div>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<div class="bb-my-avatar-others" id="bb-my-avatar-others"></div>' +
      '<div class="bb-my-avatar-counts" id="bb-my-avatar-counts">' +
        '<span id="bb-preview-room-count">0</span> people here' +
      '</div>' +
      '<div class="bb-my-avatar-status" id="bb-my-avatar-status"></div>';
    
    // Move the queue avatars into our box
    if (queueAvatarsContainer) {
      var othersContainer = myAvatarPreview.querySelector('#bb-my-avatar-others');
      if (othersContainer) {
        othersContainer.appendChild(queueAvatarsContainer);
      }
    }
    
    // Move the status text into our box
    if (queueCountText) {
      var statusContainer = myAvatarPreview.querySelector('#bb-my-avatar-status');
      if (statusContainer) {
        statusContainer.appendChild(queueCountText);
      }
    }
    
    // Make sure it's visible during loading
    if (myStatus === 'loading') {
      myAvatarPreview.style.display = 'flex';
    }
    
    console.log('🎨 Applied custom cursor color:', playerColor.name || playerColor.bg);
  }
  
  function updateMyCursor() {
    applyCustomCursor();
  }
  
  function hideMyAvatarPreview() {
    if (myAvatarPreview) {
      myAvatarPreview.style.display = 'none';
    }
  }
  
  // ========================================================================
  // EVENT BINDING
  // ========================================================================
  
  // Firebase cursor sync - throttled write
  var lastCursorWriteTime = 0;
  var CURSOR_WRITE_THROTTLE = 100; // Write to Firebase every 100ms for smoother tracking
  var currentCursorX = 0;
  var currentCursorY = 0;
  
  function writeCursorToFirebase(x, y) {
    if (!firebaseDb || !myRoomId || !Player) {
      // Debug: Log why cursor write was skipped
      if (!firebaseDb) console.warn('⚠️ Cursor write skipped: firebaseDb not initialized');
      if (!myRoomId) console.warn('⚠️ Cursor write skipped: myRoomId not set');
      if (!Player) console.warn('⚠️ Cursor write skipped: Player not initialized');
      return;
    }
    
    var now = Date.now();
    if (now - lastCursorWriteTime < CURSOR_WRITE_THROTTLE) return;
    lastCursorWriteTime = now;
    
    // Store cursor position in Firebase for admin panel to read
    import('https://www.gstatic.com/firebasejs/10.7.1/firebase-database.js').then(function(firebaseDatabase) {
      var odId = Player.getName();
      var cursorRef = firebaseDatabase.ref(firebaseDb, 'cursors/' + myRoomId + '/' + odId);
      
      firebaseDatabase.set(cursorRef, {
        x: x,
        y: y,
        sprite: Player.getSprite ? Player.getSprite().emoji : '🎴',
        color: Player.getColor ? Player.getColor().bg : '#fe5f55',
        name: Player.getName(),
        status: myStatus,
        timestamp: now
      });
    });
  }
  
  function clearCursorFromFirebase() {
    if (!firebaseDb || !myRoomId || !Player) return;
    
    import('https://www.gstatic.com/firebasejs/10.7.1/firebase-database.js').then(function(firebaseDatabase) {
      var odId = Player.getName();
      var cursorRef = firebaseDatabase.ref(firebaseDb, 'cursors/' + myRoomId + '/' + odId);
      firebaseDatabase.remove(cursorRef);
    });
  }
  
  function bindEvents() {
    document.addEventListener('mousemove', function(e) {
      // Track cursor in both loading and playing states
      if (!isActive) return;
      
      // Normalize coordinates (0-1) for broadcasting
      var x = e.clientX / window.innerWidth;
      var y = e.clientY / window.innerHeight;
      currentCursorX = x;
      currentCursorY = y;
      
      // Broadcast to Liveblocks (for real-time cursor display to other users)
      if (room && myStatus === 'loading') {
        room.updatePresence({
          cursor: { x: x, y: y },
          status: myStatus
        });
      }
      
      // Write to Firebase (for admin panel - works in both loading AND playing)
      writeCursorToFirebase(x, y);
    });
    
    // Handle visibility change to stop broadcasting when tab hidden
    document.addEventListener('visibilitychange', function() {
      if (document.hidden && room) {
        // Optional: could clear cursor
      }
    });
    
    // Clean up cursor on page unload and mark reservation for expiry
    window.addEventListener('beforeunload', function() {
      // #region agent log - H4 beforeunload tracking
      if (window._bbDebugLog) window._bbDebugLog('bb-multiplayer.js:beforeunload', 'Page unloading', { isActive: isActive, hasFirebase: !!firebaseDb }, 'H4');
      // #endregion
      
      // Gracefully disconnect Firebase to prevent XMLHttpRequest errors
      // The .lp (long-polling) disconnect error is benign but looks bad in console
      if (firebaseDb) {
        try {
          import('https://www.gstatic.com/firebasejs/10.7.1/firebase-database.js').then(function(firebaseDatabase) {
            firebaseDatabase.goOffline(firebaseDb);
          }).catch(function() {});
        } catch (e) {
          // Ignore - page is unloading anyway
        }
      }
      
      clearCursorFromFirebase();
      
      // Notify server that user is leaving (sets 30-min expiry on reservation)
      // Use sendBeacon for reliable delivery during page unload
      if (navigator.sendBeacon && Player) {
        var blob = new Blob(
          [JSON.stringify({ odId: Player.getName(), permanent: false })],
          { type: 'application/json' }
        );
        navigator.sendBeacon(FIREBASE_FUNCTIONS_URL + '/leaveQueue', blob);
      }
    });
    
    // Enter Bundle Builder button click (appears when batch is released)
    document.querySelectorAll('[id^="bb-queue-enter-"]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        console.log('🚀 [BB.Multiplayer] User clicked Enter button');
        enterGame();
        triggerQueueCallbacks('onRelease');
      });
    });
  }
  
  // ========================================================================
  // RENDER LOOP
  // ========================================================================
  
  function startRenderLoop() {
    console.log('🎬 Starting render loop');
    function loop(time) {
      renderFrame(time);
      animationFrameId = requestAnimationFrame(loop);
    }
    animationFrameId = requestAnimationFrame(loop);
  }
  
  function renderFrame(time) {
    try {
      frameCount++;
      
      // PERFORMANCE FIX: Skip render entirely when not active and no peers
      // This avoids CPU work when multiplayer isn't being used
      if (!isActive && peerData.size === 0 && myStatus !== 'playing') {
        return;
      }
      
      // Log once every 5 seconds to confirm loop is alive
      if (time - lastRenderTime > 5000) {
        console.log('🔄 Render loop heartbeat. Peers:', peerData.size);
        lastRenderTime = time;
      }
      
      // Try to find/create avatar layer if missing
      if (!avatarLayer) {
        // Append to bar container (not progress wrapper) so avatars stay aligned with the track
        var barContainer = document.querySelector('.bb-tier-bar-container');
        var progressWrapper = document.querySelector('.bb-tier-progress-wrapper');
        if (barContainer && progressWrapper) {
          avatarLayer = document.getElementById('bb-avatar-layer');
          if (!avatarLayer) {
            avatarLayer = document.createElement('div');
            avatarLayer.id = 'bb-avatar-layer';
            avatarLayer.className = 'bb-avatar-layer';
            barContainer.style.position = 'relative';
            barContainer.appendChild(avatarLayer);
            console.log('✅ Avatar layer created inside bar container');
            // Cache initial bar width - use full progress wrapper width for avatar movement
            avatarBarWidth = progressWrapper.offsetWidth || 300;
          }
        }
      }

      if (!cursorLayer && !avatarLayer) return;
      
      // Smooth interpolation factor
      var LERP_FACTOR = 0.2;
      var LOCAL_LERP_FACTOR = 0.35;
      
      // PERFORMANCE: Only render peers every N frames (30fps instead of 60fps)
      var shouldRenderPeers = (frameCount % PEER_RENDER_INTERVAL === 0);
      
      // Track visible peer count for limiting
      var visibleCursorCount = 0;
      var visibleAvatarCount = 0;
      
      // Render peers (throttled for non-flying, 60fps for flying)
      peerData.forEach(function(peer, connectionId) {
        // Always interpolate positions (keeps animation smooth)
        peer.currentX += (peer.x - peer.currentX) * LERP_FACTOR;
        peer.currentY += (peer.y - peer.currentY) * LERP_FACTOR;
        peer.currentAvatarX += (peer.avatarX - peer.currentAvatarX) * LERP_FACTOR;
        
        // EASTER EGG: Flying peers always render at 60fps for smooth gameplay
        var isFlying = peer.isFlying || (peer.presence && peer.presence.isFlying);
        
        // Only update DOM on throttled frames (unless flying)
        if (!shouldRenderPeers && !isFlying) return;
        
        // Render Cursor (Loading Screen)
        if (peer.presence.status === 'loading' && cursorLayer) {
          // PERFORMANCE: Limit visible cursors
          if (visibleCursorCount >= MAX_VISIBLE_PEERS) {
            if (peer.cursorEl) peer.cursorEl.style.display = 'none';
            return;
          }
          renderPeerCursor(connectionId, peer);
          visibleCursorCount++;
        } else {
          if (peer.cursorEl) peer.cursorEl.style.display = 'none';
        }
        
        // Render Avatar (Progress Bar OR Flying)
        if ((peer.presence.status === 'playing' || isFlying) && avatarLayer) {
          // PERFORMANCE: Limit visible avatars (but never limit flying players)
          if (!isFlying && visibleAvatarCount >= MAX_VISIBLE_PEERS) {
            if (peer.avatarEl) peer.avatarEl.style.display = 'none';
            return;
          }
          renderPeerAvatar(connectionId, peer);
          if (!isFlying) visibleAvatarCount++;
        } else {
          if (peer.avatarEl) peer.avatarEl.style.display = 'none';
        }
      });
      
      // Render MY Local Avatar with smooth interpolation (ALWAYS 60fps - no throttle)
      // Skip if fly mode is active (avatar is being controlled by keyboard)
      if (myStatus === 'playing' && !flyModeActive) {
        ensureMyAvatarExists();
        
        // Smooth interpolate toward target position
        myCurrentAvatarX += (myTargetAvatarX - myCurrentAvatarX) * LOCAL_LERP_FACTOR;
        
        // Update visual position using GPU-accelerated transform
        if (myProgressAvatar) {
          // Cap max position to account for avatar width (50px)
          var maxX = avatarBarWidth - 50;
          var xPos = Math.round(myCurrentAvatarX * maxX); // Round to int for performance
          myProgressAvatar.style.transform = 'translate3d(' + xPos + 'px, 0, 0)';
        }
      }
      
      // Update particles (for peer particles when local user isn't flying)
      // Only if there are any active particles in the pool
      if (particlePoolInitialized && !flyModeActive) {
        updateFlyParticles();
      }
    } catch (e) {
      console.error('🔴 Render Loop Error:', e);
    }
  }
  
  function renderPeerCursor(connectionId, peer) {
    var el = peer.cursorEl;
    
    // Calculate pixel positions using cached dimensions
    var x = Math.round(peer.currentX * cachedWindowWidth);
    var y = Math.round(peer.currentY * cachedWindowHeight);
    
    // PERFORMANCE: Skip if position hasn't changed enough
    if (el && peer.lastRenderedX !== undefined) {
      var dx = Math.abs(x - peer.lastRenderedX);
      var dy = Math.abs(y - peer.lastRenderedY);
      if (dx < MOVEMENT_THRESHOLD && dy < MOVEMENT_THRESHOLD) {
        return; // Skip this render - cursor hasn't moved visibly
      }
    }
    
    if (!el) {
      el = document.getElementById('cursor-' + connectionId);
      if (!el) {
        el = document.createElement('div');
        el.id = 'cursor-' + connectionId;
        el.className = 'bb-mp-cursor';
        el.style.willChange = 'transform'; // Hint to browser
        el.style.contain = 'layout style'; // CSS containment
        
        var playerName = peer.presence.name || ('Player ' + (connectionId % 100));
        var playerColor = peer.presence.color || { bg: '#fe5f55', text: '#ffffff' };
        var playerSprite = peer.presence.sprite || { emoji: '🎴' };
        
        el.innerHTML = 
          '<svg class="bb-mp-cursor-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19841L11.7841 12.3673H5.65376Z" fill="' + playerColor.bg + '" stroke="white"/>' +
          '</svg>' +
          '<div class="bb-mp-cursor-sprite">' + playerSprite.emoji + '</div>' +
          '<div class="bb-mp-cursor-label" style="background:' + playerColor.bg + ';color:' + playerColor.text + '">' + playerName + '</div>';
        cursorLayer.appendChild(el);
      }
      peer.cursorEl = el;
    } else {
      el.style.display = 'block';
      
      // Only update DOM if data actually changed
      if (peer.lastRenderedName !== peer.presence.name || 
          peer.lastRenderedColor !== peer.presence.color) {
        
        var playerName = peer.presence.name || ('Player ' + (connectionId % 100));
        var playerColor = peer.presence.color || { bg: '#fe5f55', text: '#ffffff' };
        var playerSprite = peer.presence.sprite || { emoji: '🎴' };
        
        var label = el.querySelector('.bb-mp-cursor-label');
        if (label) {
          label.textContent = playerName;
          label.style.background = playerColor.bg;
          label.style.color = playerColor.text;
        }
        var path = el.querySelector('path');
        if (path) path.setAttribute('fill', playerColor.bg);
        var sprite = el.querySelector('.bb-mp-cursor-sprite');
        if (sprite) sprite.textContent = playerSprite.emoji;
        
        peer.lastRenderedName = peer.presence.name;
        peer.lastRenderedColor = peer.presence.color;
      }
    }
    
    // Update transform and cache position
    el.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
    peer.lastRenderedX = x;
    peer.lastRenderedY = y;
  }
  
  function renderPeerAvatar(connectionId, peer) {
    var el = peer.avatarEl;
    
    // Calculate position early for movement threshold check
    var maxX = avatarBarWidth - 50;
    var xPos = Math.round(peer.currentAvatarX * maxX);
    
    // PERFORMANCE: Skip if position hasn't changed enough (for non-flying avatars)
    if (el && !peer.isFlying && peer.lastRenderedAvatarX !== undefined) {
      var dx = Math.abs(xPos - peer.lastRenderedAvatarX);
      if (dx < MOVEMENT_THRESHOLD) {
        return; // Skip this render - avatar hasn't moved visibly
      }
    }
    
    var playerName = peer.presence.name || ('Player ' + (connectionId % 100));
    var playerColor = peer.presence.color || { bg: '#fe5f55', text: '#ffffff' };
    var teamEmoji = peer.presence.teamEmoji || '🃏';
    
    var tierSprite = peer.presence.tierSprite;
    var displayEmoji = tierSprite ? tierSprite.emoji : (peer.presence.sprite ? peer.presence.sprite.emoji : '🥚');
    var displayScale = tierSprite ? tierSprite.scale : 1.0;
    
    if (!el) {
      if (!avatarLayer) return;
      
      el = document.getElementById('avatar-' + connectionId);
      if (!el) {
        el = document.createElement('div');
        el.id = 'avatar-' + connectionId;
        el.className = 'bb-mp-avatar';
        el.style.willChange = 'transform';
        el.style.contain = 'layout style'; // CSS containment for performance
        el.innerHTML = '<div class="bb-mp-avatar-sprite" style="transform:scale(' + displayScale + '); --avatar-scale:' + displayScale + '">' + displayEmoji + '</div>' +
          '<div class="bb-mp-avatar-name">' +
            '<span class="bb-mp-avatar-team">' + teamEmoji + '</span>' +
            playerName + 
          '</div>';
        avatarLayer.appendChild(el);
        parseEmojis(el); // Convert to Twemoji
      }
      peer.avatarEl = el;
    } else {
      el.style.display = 'flex';
      
      // Update only if changed
      if (peer.lastRenderedEmoji !== displayEmoji || 
          peer.lastRenderedScale !== displayScale) {
        
        var spriteEl = el.querySelector('.bb-mp-avatar-sprite');
        if (spriteEl) {
          spriteEl.textContent = displayEmoji;
          spriteEl.style.transform = 'scale(' + displayScale + ')';
          spriteEl.style.setProperty('--avatar-scale', displayScale);
          parseEmojis(spriteEl); // Convert to Twemoji
        }
        peer.lastRenderedEmoji = displayEmoji;
        peer.lastRenderedScale = displayScale;
      }
      
      if (peer.lastRenderedName !== playerName || 
          peer.lastRenderedColor !== playerColor ||
          peer.lastRenderedTeamEmoji !== teamEmoji) {
        
        var nameEl = el.querySelector('.bb-mp-avatar-name');
        if (nameEl) {
          // Update innerHTML to preserve team badge structure
          nameEl.innerHTML = '<span class="bb-mp-avatar-team">' + teamEmoji + '</span>' + playerName;
          parseEmojis(nameEl); // Convert to Twemoji
        }
        peer.lastRenderedName = playerName;
        peer.lastRenderedColor = playerColor;
        peer.lastRenderedTeamEmoji = teamEmoji;
      }
    }
    
    // Check flying state (xPos already calculated at top of function)
    if (peer.isFlying) {
       // Ensure it's in the fly container and has class
       var flyContainer = document.querySelector('.bundle-builder') || document.body;
       if (!el.classList.contains('bb-fly-mode')) {
         el.classList.add('bb-fly-mode');
         // Set styles directly to match local flying avatar
         el.style.position = 'absolute';
         el.style.top = '0';
         el.style.left = '0';
         el.style.margin = '0';
         el.style.zIndex = '5';
         el.style.pointerEvents = 'none';
         
         flyContainer.appendChild(el);
         
         // Initialize previous position for velocity tracking
         peer.prevFlyX = peer.flyX || 0;
         peer.prevFlyY = peer.flyY || 0;
       } else if (el.parentElement !== flyContainer) {
         flyContainer.appendChild(el);
       }

       // Interpolate
       var lerp = 0.1; // Smooth factor
       peer.currentFlyX = peer.currentFlyX || peer.flyX || 0;
       peer.currentFlyY = peer.currentFlyY || peer.flyY || 0;
       
       var prevX = peer.currentFlyX;
       var prevY = peer.currentFlyY;
       
       peer.currentFlyX += ((peer.flyX || 0) - peer.currentFlyX) * lerp;
       peer.currentFlyY += ((peer.flyY || 0) - peer.currentFlyY) * lerp;
       
       var containerW = flyContainer.offsetWidth;
       var containerH = flyContainer.offsetHeight;
       
       var x = peer.currentFlyX * containerW;
       var y = peer.currentFlyY * containerH;
       
       // Calculate velocity from position delta (in pixels)
       // Scale up to approximate actual physics velocity (lerp creates tiny deltas)
       var velX = (peer.currentFlyX - prevX) * containerW * 10;
       var velY = (peer.currentFlyY - prevY) * containerH * 10;
       var speed = Math.sqrt(velX * velX + velY * velY);
       
       // Spawn particles for this peer with their unique color palette (isLocal = false for shorter trails)
       if (speed > 1) {
         var spawnCount = Math.floor(speed * 0.2) + 1;
         
         // Assign a random color palette to this peer if not already assigned
         if (!playerParticleColors[connectionId]) {
           playerParticleColors[connectionId] = Math.floor(Math.random() * PARTICLE_COLOR_PALETTES.length);
         }
         var peerPalette = PARTICLE_COLOR_PALETTES[playerParticleColors[connectionId]];
         
         for (var pi = 0; pi < spawnCount; pi++) {
           if (Math.random() < 0.5) {
             var peerColor = peerPalette[Math.floor(Math.random() * peerPalette.length)];
             spawnParticleAt(x, y, velX, velY, peerColor, false);
           }
         }
       }
       
       // Center offset (assuming 50px)
       el.style.transform = 'translate3d(' + (x - 25) + 'px, ' + (y - 25) + 'px, 0)';
       
    } else {
       // Normal mode
       if (el.classList.contains('bb-fly-mode')) {
         el.classList.remove('bb-fly-mode');
         if (avatarLayer) avatarLayer.appendChild(el);
         // Reset styles
         el.style.position = '';
         el.style.top = '';
         el.style.left = '';
         el.style.margin = '';
         el.style.zIndex = '';
         el.style.pointerEvents = '';
       }
       
       el.style.transform = 'translate3d(' + xPos + 'px,0,0)';
       peer.lastRenderedAvatarX = xPos; // Cache for movement threshold
    }
  }
  
  function ensureMyAvatarExists() {
    if (!avatarLayer) return;
    
    // Check if myProgressAvatar is detached or missing
    if (!myProgressAvatar || !document.body.contains(myProgressAvatar)) {
      console.log('🔧 Recreating detached local avatar');
      
      var playerName = Player ? Player.getName() : 'Player';
      var playerColor = Player ? Player.getColor() : { bg: '#fe5f55', text: '#ffffff' };
      var playerChain = Player ? Player.getSelectedChain() : null;
      var teamEmoji = playerChain ? playerChain.baseEmoji : '🃏';
      
      var tierSprite = Player ? Player.getTierSprite() : null;
      var displayEmoji = tierSprite ? tierSprite.emoji : '🥚';
      var displayScale = tierSprite ? tierSprite.scale : 1.0;
      
      if (myProgressAvatar) myProgressAvatar.remove();
      
      myProgressAvatar = document.createElement('div');
      myProgressAvatar.id = 'bb-my-progress-avatar';
      myProgressAvatar.className = 'bb-mp-avatar bb-mp-avatar-me';
      myProgressAvatar.style.willChange = 'transform';
      myProgressAvatar.innerHTML = 
        '<div class="bb-mp-avatar-sprite" style="transform:scale(' + displayScale + '); --avatar-scale:' + displayScale + '">' + displayEmoji + '</div>' +
        '<div class="bb-mp-avatar-name">' +
          '<span class="bb-mp-avatar-team">' + teamEmoji + '</span>' +
          playerName + 
        '</div>';
      avatarLayer.appendChild(myProgressAvatar);
      parseEmojis(myProgressAvatar); // Convert to Twemoji
      
      // Cap max position to account for avatar width (50px)
      var maxX = avatarBarWidth - 50;
      var xPos = myCurrentAvatarX * maxX;
      myProgressAvatar.style.transform = 'translate3d(' + xPos + 'px, 0, 0)';
    }
  }
  
  // Placeholder for compatibility
  function renderCursors() {} 
  function renderAvatars() {}
  
  // ========================================================================
  // AVATAR ACTIONS
  // ========================================================================
  
  var isJumping = false;
  var jumpTimeoutId = null;
  var JUMP_DURATION = 600;
  var MAX_JUMP_TIME = 800; // Safety fallback - always reset after this time
  
  function resetJumpState() {
    if (myProgressAvatar) {
      myProgressAvatar.classList.remove('bb-avatar-jump');
    }
    isJumping = false;
    if (jumpTimeoutId) {
      clearTimeout(jumpTimeoutId);
      jumpTimeoutId = null;
    }
  }
  
  function jumpMyAvatar(productName) {
    if (!myProgressAvatar) return;
    
    // Get player color for message styling
    var playerColor = Player ? Player.getColor() : { bg: '#fe5f55', text: '#ffffff' };
    
    // Broadcast the jump to others (include product name and color if provided)
    if (room) {
      room.broadcastEvent({
        type: 'PLAYER_JUMP',
        connectionId: room.getSelf().connectionId,
        productName: productName || null,
        color: playerColor
      });
    }
    
    // Show product message above avatar (if product name provided)
    if (productName) {
      showAvatarMessage(myProgressAvatar, productName, playerColor);
    }
    
    // If already jumping, don't restart - let it complete (non-interruptible)
    if (isJumping) {
      console.log('🦘 Jump in progress, skipping - evolve CSS will handle flash');
      return;
    }
    
    // Start jump (non-interruptible)
    console.log('🦘 Starting jump');
    isJumping = true;
    myProgressAvatar.classList.add('bb-avatar-jump');
    
    // End jump after duration
    jumpTimeoutId = setTimeout(resetJumpState, JUMP_DURATION);
    
    // SAFETY FALLBACK - always reset state after max time
    setTimeout(function() {
      if (isJumping) {
        console.log('⚠️ Jump state stuck, forcing reset');
        resetJumpState();
      }
    }, MAX_JUMP_TIME);
  }
  
  // Show floating product message above an avatar
  function showAvatarMessage(avatarEl, message, color) {
    if (!avatarEl || !message) return;
    
    // Remove any existing message from this avatar
    var existingMsg = avatarEl.querySelector('.bb-mp-avatar-message');
    if (existingMsg) {
      existingMsg.remove();
    }
    
    // Create the message element with player color
    var playerColor = color || { bg: '#fe5f55', text: '#ffffff' };
    var msgEl = document.createElement('div');
    msgEl.className = 'bb-mp-avatar-message';
    var textContent = 'Added ' + message;
    msgEl.textContent = textContent;
    msgEl.setAttribute('data-text', textContent); // For white text overlay via ::after
    msgEl.style.color = playerColor.bg; // Use player's assigned color for glow
    avatarEl.appendChild(msgEl);
    
    // Force reflow then add animation class
    void msgEl.offsetWidth;
    msgEl.classList.add('bb-message-animate');
    
    // Remove after animation completes (2.5s)
    setTimeout(function() {
      if (msgEl.parentNode) {
        msgEl.remove();
      }
    }, 2500);
  }
  
  
  function evolveMyAvatar(cardCount) {
    if (!Player) return null;
    
    var result = Player.setTierFromCardCount(cardCount);
    
    var tierChanged = result.evolved || result.devolved;
    if (!tierChanged) return result;
    
    var tierSprite = result.sprite;
    
    // Update local avatar - ONLY handles emoji change + flash (NOT the jump)
    // This layer CAN be interrupted for rapid emoji changes
    if (myProgressAvatar) {
      var spriteEl = myProgressAvatar.querySelector('.bb-mp-avatar-sprite');
      if (spriteEl) {
        // Remove evolve classes only (NOT bb-avatar-jump - that's a separate layer)
        myProgressAvatar.classList.remove('bb-avatar-evolve', 'bb-avatar-devolve', 'bb-avatar-legendary');
        
        // Force animation restart on BOTH parent and sprite
        spriteEl.style.animation = 'none';
        void spriteEl.offsetWidth; // Force reflow on sprite specifically
        spriteEl.style.animation = ''; // Let CSS take over
        
        if (result.isFinalForm) {
          myProgressAvatar.classList.add('bb-avatar-legendary');
          console.log('🐉 LEGENDARY EVOLUTION! Stage:', result.stage);
        } else if (result.evolved) {
          myProgressAvatar.classList.add('bb-avatar-evolve');
          console.log('⬆️ EVOLVED to stage', result.stage, tierSprite.name);
        } else if (result.devolved) {
          myProgressAvatar.classList.add('bb-avatar-devolve');
          console.log('⬇️ DEVOLVED to stage', result.stage, tierSprite.name);
        }
        
        // Update emoji immediately (this CAN be interrupted by rapid selections)
        spriteEl.textContent = tierSprite.emoji;
        spriteEl.style.transform = 'scale(' + tierSprite.scale + ')';
        spriteEl.style.setProperty('--avatar-scale', tierSprite.scale);
        parseEmojis(spriteEl); // Convert to Twemoji
      }
      
      setTimeout(function() {
        myProgressAvatar.classList.remove('bb-avatar-evolve', 'bb-avatar-devolve', 'bb-avatar-legendary');
      }, 2500);
    }
    
    // Broadcast to others
    if (room) {
      room.updatePresence({ 
        tier: result.tier,
        stage: result.stage,
        tierSprite: tierSprite
      });
      
      room.broadcastEvent({
        type: 'PLAYER_EVOLVE',
        connectionId: room.getSelf().connectionId,
        tier: result.tier,
        stage: result.stage,
        evolved: result.evolved,
        devolved: result.devolved,
        isFinalForm: result.isFinalForm,
        tierSprite: tierSprite
      });
    }
    
    return result;
  }
  
  // ========================================================================
  // AVATAR POSITION
  // ========================================================================
  
  var lastBroadcastTime = 0;
  var pendingBroadcast = null;
  var BROADCAST_THROTTLE = 50;
  
  function updateAvatarPositionLocal(normalizedX) {
    myTargetAvatarX = Math.max(0, Math.min(1, normalizedX));
    myAvatarPosition = myTargetAvatarX;
    
    // Throttled broadcast to others
    var now = Date.now();
    if (now - lastBroadcastTime >= BROADCAST_THROTTLE) {
      lastBroadcastTime = now;
      if (room) {
        room.updatePresence({ avatarX: myAvatarPosition });
      }
    } else if (!pendingBroadcast) {
      pendingBroadcast = setTimeout(function() {
        pendingBroadcast = null;
        lastBroadcastTime = Date.now();
        if (room) {
          room.updatePresence({ avatarX: myAvatarPosition });
        }
      }, BROADCAST_THROTTLE);
    }
  }
  
  function updateAvatarPosition(normalizedX) {
    updateAvatarPositionLocal(normalizedX);
  }
  
  // ========================================================================
  // STATUS
  // ========================================================================
  
  function updateStatusCount() {
    // DEDUPLICATE others by userId (same user with multiple tabs = 1 user)
    // Liveblocks returns separate entries for each connection/tab
    var uniqueOthers = [];
    var seenUserIds = new Set();
    others.forEach(function(u) {
      // Use the user's info.name or id as the unique identifier
      var userId = (u.info && u.info.name) || u.connectionId;
      if (!seenUserIds.has(userId)) {
        seenUserIds.add(userId);
        uniqueOthers.push(u);
      }
    });
    
    // Count ALL unique others in room (regardless of status)
    var allOthersInRoom = uniqueOthers.filter(function(u) {
      return u.presence;
    }).length;
    
    // Count only those still WAITING (status === 'loading')
    var othersWaiting = uniqueOthers.filter(function(u) {
      return u.presence && u.presence.status === 'loading';
    }).length;
    
    // Total in room = all unique others + self
    var totalInRoom = allOthersInRoom + 1;
    
    // Total waiting in THIS room = others waiting + (self if I'm still waiting)
    var totalWaitingInRoom = othersWaiting + (myStatus === 'loading' ? 1 : 0);
    
    // Update new queue panel elements (for all section IDs)
    // Room NAME (not number)
    var roomNumberEls = document.querySelectorAll('[id^="bb-room-number-"]');
    roomNumberEls.forEach(function(el) {
      el.textContent = getRoomName(myBatchNumber);
    });
    
    // Update room name in enter button
    var enterRoomEls = document.querySelectorAll('[id^="bb-enter-room-"]');
    enterRoomEls.forEach(function(el) {
      el.textContent = 'room ' + getRoomName(myBatchNumber);
    });
    
    // Users in room (show ALL users, not just waiting)
    var roomUsersEls = document.querySelectorAll('[id^="bb-room-users-"]');
    roomUsersEls.forEach(function(el) {
      el.textContent = totalInRoom;
    });
    
    // Update preview counts in avatar preview
    var previewRoomCount = document.getElementById('bb-preview-room-count');
    if (previewRoomCount) {
      previewRoomCount.textContent = totalInRoom;
    }
    
    // Total waiting - use LIVE count of loading users only
    var totalWaitingEls = document.querySelectorAll('[id^="bb-total-waiting-"]');
    var totalOnline = liveTotalWaiting || totalWaitingInRoom;
    totalWaitingEls.forEach(function(el) {
      // liveTotalWaiting comes from Firebase/admin endpoint - users with status 'loading'
      el.textContent = totalOnline;
    });
    
    // Update preview total count
    var previewTotalCount = document.getElementById('bb-preview-total-count');
    if (previewTotalCount) {
      previewTotalCount.textContent = totalOnline;
    }
    
    // Show room info panel immediately once we have a room (don't wait for loading bar)
    if (myBatchNumber) {
      var roomInfoEls = document.querySelectorAll('[id^="bb-queue-room-info-"]');
      roomInfoEls.forEach(function(el) {
        el.classList.remove('bb-hidden');
      });
      
      var queuePeopleEls = document.querySelectorAll('[id^="bb-queue-people-"]');
      queuePeopleEls.forEach(function(el) {
        el.classList.remove('bb-hidden');
      });
    }
    
    // Check if loading is complete (from bundle-builder.js)
    var loadingComplete = window.BB && window.BB.isLoadingComplete && window.BB.isLoadingComplete();
    
    // SIMPLIFIED: Check if simple entry countdown is active
    var simpleCountdownActive = isSimpleCountdownActive();
    var simpleCountdownSecs = getSimpleCountdownRemaining();
    
    // Queue count text - SIMPLIFIED messaging
    var queueCountTextEls = document.querySelectorAll('[id^="bb-queue-count-text-"]');
    queueCountTextEls.forEach(function(el) {
      if (isReleased) {
        // Ready to enter!
        el.classList.remove('bb-countdown-active');
        if (!loadingComplete) {
          el.innerHTML = '🎉 <strong>Ready!</strong> Loading...';
        } else {
          el.innerHTML = '🎉 <strong>Ready to enter!</strong>';
        }
      } else if (simpleCountdownActive) {
        // Show the simple countdown (no pulse animation)
        el.innerHTML = '<div class="bb-countdown-display" style="animation: none !important;">' +
          '<span class="bb-countdown-label" style="animation: none !important;">🎉 ENTERING IN</span>' +
          '<span class="bb-countdown-timer" style="animation: none !important;">0:' + String(simpleCountdownSecs).padStart(2, '0') + '</span>' +
          '</div>';
        el.classList.remove('bb-countdown-active'); // No pulse
      } else {
        // Fallback - should rarely happen
        el.classList.remove('bb-countdown-active');
        el.innerHTML = '🎉 <strong>Getting ready...</strong>';
      }
    });
    
    // Enter button visibility - SIMPLIFIED:
    // 1. Countdown complete (isReleased)
    // 2. User is still waiting (myStatus === 'loading')
    // 3. Loading is complete (loadingComplete)
    var canShowEnterButton = isReleased && myStatus === 'loading' && loadingComplete;
    
    var enterBtns = document.querySelectorAll('[id^="bb-queue-enter-"]');
    enterBtns.forEach(function(el) {
      if (canShowEnterButton) {
        el.classList.remove('bb-hidden');
      } else {
        el.classList.add('bb-hidden');
      }
    });
    
    // Epilepsy warning visibility - Show/hide with Enter button
    var warnings = document.querySelectorAll('[id^="bb-queue-warning-"]');
    warnings.forEach(function(el) {
      if (canShowEnterButton) {
        el.classList.remove('bb-hidden');
      } else {
        el.classList.add('bb-hidden');
      }
    });
    
    // Update fixed room indicator (visible during experience)
    var roomIndicators = document.querySelectorAll('[id^="bb-room-indicator-"]');
    roomIndicators.forEach(function(el) {
      // Show indicator once room is assigned
      if (myBatchNumber) {
        el.classList.add('visible');
      }
    });
    
    var roomNameDisplays = document.querySelectorAll('[id^="bb-room-name-display-"]');
    roomNameDisplays.forEach(function(el) {
      el.textContent = getRoomName(myBatchNumber);
    });
    
    // Room count in indicator shows ALL users in room
    var roomCountDisplays = document.querySelectorAll('[id^="bb-room-count-display-"]');
    roomCountDisplays.forEach(function(el) {
      el.textContent = totalInRoom;
    });
    
    // Sync avatars with Liveblocks presence (max 50)
    syncQueueAvatars();
    
    // Legacy element support
    var countEl = document.getElementById('bb-mp-count');
    var positionEl = document.getElementById('bb-mp-position');
    var totalEl = document.getElementById('bb-queue-total');
    var batchEl = document.getElementById('bb-queue-batch');
    var othersInBatchEl = document.getElementById('bb-others-in-batch');
    
    if (totalEl) totalEl.textContent = liveTotalWaiting || totalInRoom;
    if (batchEl && myBatchNumber) batchEl.textContent = getRoomName(myBatchNumber);
    if (othersInBatchEl) othersInBatchEl.textContent = othersInRoom;
    if (countEl) countEl.textContent = othersInRoom;
    
    if (positionEl) {
      if (isReleased) {
        positionEl.innerHTML = '🎉 <strong>You\'re in!</strong>';
      } else {
        positionEl.innerHTML = '<span class="bb-queue-priority">Priority Queue</span> • ' + 
          globalQueueCount + ' total • ' + totalInRoom + ' in your room';
      }
    }
  }
  
  // ========================================================================
  // GAME ENTRY
  // ========================================================================
  
  function enterGame() {
    if (room) {
      myStatus = 'playing';
      
      room.updatePresence({
        // Keep cursor tracking for Firebase admin panel (don't set to null)
        cursor: { x: currentCursorX, y: currentCursorY },
        status: 'playing',
        avatarX: 0.5
      });
      
      // Hide cursor layer and avatar preview (for the waiting room visuals)
      if (cursorLayer) cursorLayer.style.display = 'none';
      hideMyAvatarPreview();
      var statusEl = document.getElementById('bb-mp-status');
      if (statusEl) statusEl.classList.add('hidden');
      
      // Reset cursor to default
      document.body.style.cursor = '';
      var styleEl = document.getElementById('bb-custom-cursor-style');
      if (styleEl) styleEl.textContent = '';
      
      // Start tracking mouse for avatar position
      startAvatarTracking();
      renderAvatars();
      
      // Write status change to Firebase
      writeCursorToFirebase(currentCursorX, currentCursorY);
    }
  }
  
  // ========================================================================
  // QUEUE AVATARS - Sync floating emojis with Liveblocks presence
  // ========================================================================
  
  function syncQueueAvatars() {
    var avatarContainers = document.querySelectorAll('[id^="bb-queue-avatars-"]');
    
    avatarContainers.forEach(function(container) {
      // Get all users in the room (including self)
      var usersInRoom = [];
      
      // Add self first
      var playerSprite = Player ? Player.getSprite() : { emoji: '🃏' };
      usersInRoom.push({ emoji: playerSprite.emoji, isSelf: true });
      
      // Add others from Liveblocks (max 49 others to make 50 total)
      var othersToShow = others.slice(0, 49);
      othersToShow.forEach(function(user) {
        if (user.presence && user.presence.sprite) {
          var emoji = typeof user.presence.sprite === 'string' 
            ? user.presence.sprite 
            : (user.presence.sprite.emoji || '🃏');
          usersInRoom.push({ emoji: emoji, isSelf: false });
        }
      });
      
      // Cap at 50 total
      usersInRoom = usersInRoom.slice(0, 50);
      
      // Update DOM - only add/remove as needed for performance
      var currentAvatars = container.querySelectorAll('.bb-queue-avatar');
      var currentCount = currentAvatars.length;
      var targetCount = usersInRoom.length;
      
      // Remove excess avatars
      for (var i = currentCount - 1; i >= targetCount; i--) {
        currentAvatars[i].classList.add('leaving');
        (function(el) {
          setTimeout(function() { el.remove(); }, 500);
        })(currentAvatars[i]);
      }
      
      // Update existing avatars
      for (var j = 0; j < Math.min(currentCount, targetCount); j++) {
        var avatar = currentAvatars[j];
        var user = usersInRoom[j];
        avatar.textContent = user.emoji;
        if (user.isSelf) {
          avatar.classList.add('is-self');
        } else {
          avatar.classList.remove('is-self');
        }
      }
      
      // Add new avatars
      for (var k = currentCount; k < targetCount; k++) {
        var user = usersInRoom[k];
        var newAvatar = document.createElement('div');
        newAvatar.className = 'bb-queue-avatar' + (user.isSelf ? ' is-self' : '');
        newAvatar.textContent = user.emoji;
        newAvatar.style.animationDelay = (k * 0.1) + 's';
        container.appendChild(newAvatar);
      }
      
      // Parse all queue avatars with Twemoji for consistent rendering
      parseEmojis(container);
    });
  }
  
  function startAvatarTracking() {
    // Use full progress wrapper for both tracking and positioning
    // Avatars should move across the entire wrapper width
    var progressWrapper = document.querySelector('.bb-tier-progress-wrapper');
    if (!progressWrapper) return;
    
    var cachedRect = progressWrapper.getBoundingClientRect();
    var lastRectUpdate = Date.now();
    
    var updateBarWidth = function() {
      // Avatar width uses full progress wrapper width
      avatarBarWidth = progressWrapper.offsetWidth || 300;
      cachedRect = progressWrapper.getBoundingClientRect();
      lastRectUpdate = Date.now();
    };
    window.addEventListener('resize', updateBarWidth);
    updateBarWidth();
    
    function getRect() {
      var now = Date.now();
      if (now - lastRectUpdate > 500) {
        cachedRect = progressWrapper.getBoundingClientRect();
        lastRectUpdate = now;
      }
      return cachedRect;
    }
    
    document.addEventListener('mousemove', function(e) {
      if (myStatus !== 'playing') return;
      
      var rect = getRect();
      var relativeX = (e.clientX - rect.left) / rect.width;
      relativeX = Math.max(0, Math.min(1, relativeX));
      updateAvatarPositionLocal(relativeX);
    });
    
    // Touch support
    document.addEventListener('touchmove', function(e) {
      if (myStatus !== 'playing' || !e.touches[0]) return;
      
      var rect = getRect();
      var relativeX = (e.touches[0].clientX - rect.left) / rect.width;
      relativeX = Math.max(0, Math.min(1, relativeX));
      updateAvatarPositionLocal(relativeX);
    });
  }
  
  // ========================================================================
  // FLY OFF
  // ========================================================================
  
  function flyOff(savings) {
    var progressWrapper = document.querySelector('.bb-tier-progress-wrapper');
    if (!progressWrapper || !avatarLayer) return;
    
    var playerName = Player ? Player.getName() : 'Player';
    var playerColor = Player ? Player.getColor() : { bg: '#fe5f55', text: '#ffffff' };
    // Use the EVOLVED tier sprite, not the base sprite
    var tierSprite = Player ? Player.getTierSprite() : null;
    var playerSprite = tierSprite || (Player ? Player.getSprite() : { emoji: '🃏' });
    var savingsAmount = savings || Math.floor(Math.random() * (507 - 92 + 1)) + 92;
    
    // Broadcast to others
    if (room) {
      room.broadcastEvent({
        type: 'PLAYER_FLY_OFF',
        name: playerName,
        color: playerColor,
        sprite: playerSprite,
        savings: savingsAmount,
        avatarX: myAvatarPosition
      });
    }
    
    // Create fly-off animation element
    createFlyOffAnimation(playerName, playerColor, playerSprite, savingsAmount, myAvatarPosition * progressWrapper.offsetWidth);
  }
  
  function createFlyOffAnimation(name, color, sprite, savings, xPos) {
    var progressWrapper = document.querySelector('.bb-tier-progress-wrapper');
    if (!progressWrapper) return;
    
    var container = document.querySelector('.bundle-builder') || document.body;
    
    // Clamp position to keep notification on screen
    // Bubble is 200px min-width, centered via transform: translateX(-50%)
    // So we need 100px margin on each side to prevent clipping
    var wrapperWidth = progressWrapper.offsetWidth;
    var bubbleHalfWidth = 120; // Half of min-width (200px) + padding buffer
    var minX = bubbleHalfWidth;
    var maxX = wrapperWidth - bubbleHalfWidth;
    var clampedX = Math.max(minX, Math.min(maxX, xPos));
    
    // Create fly element but keep bubble hidden initially
    var flyEl = document.createElement('div');
    flyEl.className = 'bb-mp-flyoff';
    var bubbleText = name + ' just saved $' + savings + '!';
    flyEl.innerHTML = '<div class="bb-mp-flyoff-sprite">' + sprite.emoji + '</div>' +
      '<div class="bb-mp-flyoff-bubble bb-flyoff-hidden" data-text="' + bubbleText + '">' +
      bubbleText + '</div>';
    flyEl.style.left = clampedX + 'px';
    
    progressWrapper.appendChild(flyEl);
    parseEmojis(flyEl);
    
    var spriteEl = flyEl.querySelector('.bb-mp-flyoff-sprite');
    
    // Start sprite animation immediately
    requestAnimationFrame(function() {
      flyEl.classList.add('bb-mp-flyoff-animate');
    });
    
    // Spawn coins from the sprite position (track it as it moves)
    // Start after a brief delay so sprite has started moving
    setTimeout(function() {
      spawnGoldCoinsFromSprite(spriteEl, container, function() {
        var bubble = flyEl.querySelector('.bb-mp-flyoff-bubble');
        if (bubble) {
          bubble.classList.remove('bb-flyoff-hidden');
          bubble.classList.add('bb-flyoff-reveal');
        }
      });
    }, 100);
    
    setTimeout(function() {
      flyEl.remove();
    }, 5000);
  }
  
  // Spawn gold coins from the flying sprite element - shoots UP, arcs down
  function spawnGoldCoinsFromSprite(spriteEl, container, onComplete) {
    if (!spriteEl || !container) {
      if (onComplete) onComplete();
      return;
    }
    
    var containerRect = container.getBoundingClientRect();
    var GRAVITY = 0.6;
    var FRICTION = 0.99;
    var SPAWN_RATE = 3;
    var DURATION = 500; // ms
    
    var coins = [];
    var startTime = Date.now();
    var isSpewing = true;
    var hasCalledComplete = false;
    
    function spawnCoin() {
      var spriteRect = spriteEl.getBoundingClientRect();
      var spawnX = spriteRect.left - containerRect.left + spriteRect.width / 2;
      var spawnY = spriteRect.top - containerRect.top + spriteRect.height / 2;
      
      var coin = document.createElement('div');
      var size = 8 + Math.random() * 4;
      // Z-index bumped to 2000 to be above progress bar container
      coin.style.cssText = 'position:absolute;pointer-events:none;z-index:2000;' +
        'width:' + size + 'px;height:' + size + 'px;background:#ffd700;' +
        'left:' + spawnX + 'px;top:' + spawnY + 'px;';
      container.appendChild(coin);
      
      // Fire UP with horizontal spread - shorter arc than before
      var vx = (Math.random() - 0.5) * 8;
      var vy = -(6 + Math.random() * 6); // Shoot UP (negative Y) - shorter arc
      
      coins.push({
        el: coin,
        baseX: spawnX,
        baseY: spawnY,
        x: spawnX,
        y: spawnY,
        vx: vx,
        vy: vy,
        life: 1.0,
        decay: 0.01 + Math.random() * 0.008
      });
    }
    
    function animate() {
      var now = Date.now();
      
      if (isSpewing) {
        for (var i = 0; i < SPAWN_RATE; i++) {
          spawnCoin();
        }
        if (now - startTime > DURATION) {
          isSpewing = false;
        }
      }
      
      // Call onComplete when particles winding down
      if (!hasCalledComplete && now - startTime > DURATION * 0.8) {
        hasCalledComplete = true;
        if (onComplete) onComplete();
      }
      
      coins = coins.filter(function(c) {
        if (c.life <= 0) {
          c.el.remove();
          return false;
        }
        
        c.x += c.vx;
        c.y += c.vy;
        c.vy += GRAVITY; // Gravity pulls them down into arc
        c.vx *= FRICTION;
        c.vy *= FRICTION;
        c.life -= c.decay;
        
        c.el.style.transform = 'translate3d(' + (c.x - c.baseX) + 'px, ' + (c.y - c.baseY) + 'px, 0)';
        c.el.style.opacity = c.life;
        
        return true;
      });
      
      if (isSpewing || coins.length > 0) {
        requestAnimationFrame(animate);
      }
    }
    
    animate();
  }
  
  function updatePlayerCustomization() {
    if (room && Player) {
      var chain = Player.getSelectedChain();
      room.updatePresence({
        name: Player.getName(),
        color: Player.getColor(),
        sprite: Player.getSprite(),
        teamEmoji: chain ? chain.baseEmoji : '🃏'
      });
      renderCursors();
    }
  }
  
  function getOthersCount() {
    // Deduplicate by userId
    var seenUserIds = new Set();
    var count = 0;
    others.forEach(function(u) {
      if (u.presence && u.presence.status === 'loading') {
        var userId = (u.info && u.info.name) || u.connectionId;
        if (!seenUserIds.has(userId)) {
          seenUserIds.add(userId);
          count++;
        }
      }
    });
    return count;
  }
  
  console.log('[BB.Multiplayer] Module loaded v' + VERSION);
  
  // ========================================================================
  // KONAMI CODE EASTER EGG - Flying Avatar Mode
  // Up Up Down Down Left Right Left Right B A
  // ========================================================================
  
  var konamiSequence = [
    'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
    'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight',
    'KeyB', 'KeyA'
  ];
  var konamiProgress = 0;
  var flyModeActive = false;
  var flyVelocity = { x: 0, y: 0 };
  var flyPosition = { x: 0, y: 0 };
  var flyKeys = { up: false, down: false, left: false, right: false };
  var flyAnimationId = null;
  var flyParticles = [];
  var particlePool = [];
  var MAX_PARTICLES = 100; // Increased to support multiplayer particles
  var particlePoolInitialized = false;
  var flyStylesEl = null;
  var flyAvatarSize = 50; // Cached tier-based avatar size, set on fly mode activation
  var FLY_CONFIG = {
    acceleration: 0.8,    // Thrust power
    gravity: 0.25,        // Constant downward pull
    maxSpeed: 20,         // Higher max speed
    friction: 0.98,       // Very slippery (drift)
    particleRate: 2,      // Particles per frame
    particleLife: 50,     // Local player particle life (longer trails)
    particleLifePeer: 20, // Other players' particle life (shorter trails)
    bounceElasticity: 0.5
  };
  
  // ========================================================================
  // FLY GAME - Hoops, Leaderboard, Collisions
  // ========================================================================
  
  var flyGameActive = false;
  var myFlyScore = 0;
  var flyLeaderboard = null;
  var flyHoopEl = null;
  var currentHoop = null; // { x, y, collected: false, spawnedBy: connectionId }
  var HOOP_SIZE = 80;
  var HOOP_SPAWN_MIN = 4000;  // Min time between hoops (ms)
  var HOOP_SPAWN_MAX = 8000;  // Max time between hoops (ms)
  var hoopSpawnTimeout = null;
  var lastCollisionTime = {}; // connectionId -> timestamp (prevent rapid re-collisions)
  var COLLISION_COOLDOWN = 500; // ms between collisions with same player
  var SHUNT_FORCE = 15; // Velocity impulse on collision
  var COLLISION_RADIUS_MULTIPLIER = 1.8; // How much bigger than avatar size for collision detection
  
  // Player particle color themes - assigned randomly per player
  var playerParticleColors = {}; // connectionId -> color palette
  var myParticleColorIndex = Math.floor(Math.random() * 6); // My random color theme
  var PARTICLE_COLOR_PALETTES = [
    ['#ff6b6b', '#ff8787', '#ffa8a8', '#ffcc00', '#ff4757'], // Red/coral fire
    ['#4ade80', '#6ee7a0', '#86efac', '#a7f3d0', '#22c55e'], // Green/mint
    ['#a855f7', '#c084fc', '#d8b4fe', '#e9d5ff', '#9333ea'], // Purple/violet
    ['#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe', '#3b82f6'], // Blue/sky
    ['#f59e0b', '#fbbf24', '#fcd34d', '#fde68a', '#d97706'], // Amber/gold
    ['#ec4899', '#f472b6', '#f9a8d4', '#fbcfe8', '#db2777']  // Pink/magenta
  ];
  
  // Initialize particle pool (shared between local and peer particles)
  function ensureParticlePool() {
    if (particlePoolInitialized) return;
    
    var flyContainer = document.querySelector('.bundle-builder') || document.querySelector('#bundle-builder') || document.body;
    
    for (var i = 0; i < MAX_PARTICLES; i++) {
      var el = document.createElement('div');
      el.className = 'bb-fly-particle';
      el.style.display = 'none';
      flyContainer.appendChild(el);
      particlePool.push({ 
        el: el, 
        active: false,
        x: 0, y: 0, vx: 0, vy: 0, life: 0, maxLife: 0, size: 0, color: '' 
      });
    }
    particlePoolInitialized = true;
  }
  
  // Spawn particle at a given position with velocity (works for local and peers)
  // isLocal: true for local player (longer life), false for peers (shorter life)
  function spawnParticleAt(posX, posY, velX, velY, colorOverride, isLocal) {
    // PERFORMANCE: Skip particles on low settings
    if (window.BBPerf && window.BBPerf.enableParticles === false) return;
    
    ensureParticlePool();
    
    // Find first inactive particle in pool
    var p = null;
    for (var i = 0; i < particlePool.length; i++) {
      if (!particlePool[i].active) {
        p = particlePool[i];
        break;
      }
    }
    
    // If no inactive, pick random to recycle
    if (!p && particlePool.length > 0) {
      p = particlePool[Math.floor(Math.random() * particlePool.length)];
    }
    
    if (!p) return;
    
    // Setup particle - local player gets longer trails, peers get shorter
    var lifespan = isLocal ? FLY_CONFIG.particleLife : FLY_CONFIG.particleLifePeer;
    p.active = true;
    p.life = lifespan;
    p.maxLife = lifespan;
    
    // Position relative to given center
    var offsetX = (Math.random() - 0.5) * 20;
    var offsetY = (Math.random() - 0.5) * 20;
    p.x = posX + offsetX;
    p.y = posY + offsetY;
    
    // Velocity opposite to movement (trail effect) + randomness
    p.vx = -velX * 0.4 + (Math.random() - 0.5) * 2;
    p.vy = -velY * 0.4 + (Math.random() - 0.5) * 2;
    
    // Appearance
    var size = 4 + Math.random() * 6;
    var defaultColors = ['#ffcc00', '#ffd700', '#fe5f55', '#ff8c42', '#fff4cc', '#ffaa00'];
    var color = colorOverride || defaultColors[Math.floor(Math.random() * defaultColors.length)];
    
    p.el.style.display = 'block';
    p.el.style.width = size + 'px';
    p.el.style.height = size + 'px';
    p.el.style.background = color;
    p.el.style.boxShadow = '0 0 ' + (size * 2) + 'px ' + color;
    p.el.style.transform = 'translate3d(' + p.x + 'px, ' + p.y + 'px, 0)';
    p.el.style.opacity = '1';
  }
  
  // ========================================================================
  // FLY GAME - Leaderboard UI
  // ========================================================================
  
  function createFlyLeaderboard() {
    if (flyLeaderboard) return;
    
    var container = document.querySelector('.bundle-builder') || document.body;
    
    flyLeaderboard = document.createElement('div');
    flyLeaderboard.className = 'bb-fly-leaderboard';
    flyLeaderboard.innerHTML = 
      '<div class="bb-fly-leaderboard-header">' +
        '<span class="bb-fly-leaderboard-title">🏆 HOOP CHASE</span>' +
        '<span class="bb-fly-leaderboard-subtitle">First through the hoop scores!</span>' +
      '</div>' +
      '<div class="bb-fly-leaderboard-list" id="bb-fly-leaderboard-list"></div>' +
      '<div class="bb-fly-leaderboard-controls">' +
        '<span class="bb-fly-control">↑↓←→ / WASD to fly</span>' +
        '<span class="bb-fly-control">ESC to exit</span>' +
      '</div>';
    
    container.appendChild(flyLeaderboard);
    
    // Initial render
    updateFlyLeaderboard();
  }
  
  function updateFlyLeaderboard() {
    if (!flyLeaderboard) return;
    
    var listEl = flyLeaderboard.querySelector('#bb-fly-leaderboard-list');
    if (!listEl) return;
    
    // Collect all flying players and their scores
    var players = [];
    
    // Add self - use evolved tier sprite only
    var myName = Player ? Player.getName() : 'You';
    var myTierSprite = Player ? Player.getTierSprite() : { emoji: '🎴' };
    var myColor = Player ? Player.getColor() : { bg: '#fe5f55' };
    players.push({
      name: myName,
      sprite: myTierSprite.emoji, // Only evolved emoji, not team base
      score: myFlyScore,
      color: myColor.bg,
      isMe: true,
      isFlying: flyModeActive
    });
    
    // Add others who have activated fly game (presence.flyGameActive = true)
    others.forEach(function(user) {
      if (user.presence && user.presence.flyGameActive) {
        // Use tierSprite (evolved) if available, NOT base team sprite
        var evolvedEmoji = '🎴';
        if (user.presence.tierSprite && user.presence.tierSprite.emoji) {
          evolvedEmoji = user.presence.tierSprite.emoji;
        }
        
        players.push({
          name: user.presence.name || 'Player',
          sprite: evolvedEmoji, // Only evolved emoji
          score: user.presence.flyScore || 0,
          color: user.presence.color ? user.presence.color.bg : '#fe5f55',
          isMe: false,
          isFlying: user.presence.isFlying
        });
      }
    });
    
    // Sort by score (highest first)
    players.sort(function(a, b) { return b.score - a.score; });
    
    // Render
    var html = '';
    players.forEach(function(p, idx) {
      var rankEmoji = idx === 0 ? '🥇' : (idx === 1 ? '🥈' : (idx === 2 ? '🥉' : (idx + 1)));
      var meClass = p.isMe ? ' bb-fly-player-me' : '';
      var flyingClass = p.isFlying ? ' bb-fly-player-flying' : '';
      html += '<div class="bb-fly-player' + meClass + flyingClass + '">' +
        '<span class="bb-fly-rank">' + rankEmoji + '</span>' +
        '<span class="bb-fly-player-sprite">' + p.sprite + '</span>' +
        '<span class="bb-fly-player-name" style="color:' + p.color + '">' + p.name + (p.isMe ? ' (you)' : '') + '</span>' +
        '<span class="bb-fly-player-score">' + p.score + '</span>' +
      '</div>';
    });
    
    if (players.length === 0) {
      html = '<div class="bb-fly-empty">No players yet...</div>';
    }
    
    listEl.innerHTML = html;
  }
  
  function removeFlyLeaderboard() {
    if (flyLeaderboard) {
      flyLeaderboard.classList.add('bb-fly-leaderboard-exit');
      setTimeout(function() {
        if (flyLeaderboard) {
          flyLeaderboard.remove();
          flyLeaderboard = null;
        }
      }, 300);
    }
  }
  
  // ========================================================================
  // FLY GAME - Hoop System
  // ========================================================================
  
  function createHoopElement() {
    if (flyHoopEl) return;
    
    var container = document.querySelector('.bundle-builder') || document.body;
    
    flyHoopEl = document.createElement('div');
    flyHoopEl.className = 'bb-fly-hoop';
    flyHoopEl.innerHTML = '<div class="bb-fly-hoop-ring">⭕</div><div class="bb-fly-hoop-glow"></div>';
    flyHoopEl.style.display = 'none';
    container.appendChild(flyHoopEl);
  }
  
  function spawnHoop(fromBroadcast, hoopData) {
    if (!flyHoopEl) createHoopElement();
    
    var container = document.querySelector('.bundle-builder') || document.body;
    var containerW = container.offsetWidth;
    var containerH = container.offsetHeight;
    
    if (fromBroadcast && hoopData) {
      // Hoop position from another player
      currentHoop = {
        x: hoopData.x * containerW,
        y: hoopData.y * containerH,
        normalizedX: hoopData.x,
        normalizedY: hoopData.y,
        collected: false,
        spawnedBy: hoopData.spawnedBy,
        id: hoopData.id
      };
    } else {
      // Generate random position (avoid edges)
      var padding = HOOP_SIZE + 50;
      var x = padding + Math.random() * (containerW - padding * 2);
      var y = padding + Math.random() * (containerH - padding * 2);
      
      currentHoop = {
        x: x,
        y: y,
        normalizedX: x / containerW,
        normalizedY: y / containerH,
        collected: false,
        spawnedBy: room ? room.getSelf().connectionId : 'local',
        id: Date.now() + '_' + Math.random().toString(36).substr(2, 6)
      };
      
      // Broadcast to others
      if (room) {
        room.broadcastEvent({
          type: 'HOOP_SPAWN',
          x: currentHoop.normalizedX,
          y: currentHoop.normalizedY,
          spawnedBy: currentHoop.spawnedBy,
          id: currentHoop.id
        });
      }
    }
    
    // Position the hoop
    flyHoopEl.style.left = (currentHoop.x - HOOP_SIZE / 2) + 'px';
    flyHoopEl.style.top = (currentHoop.y - HOOP_SIZE / 2) + 'px';
    flyHoopEl.style.display = 'flex';
    flyHoopEl.classList.remove('bb-fly-hoop-collected', 'bb-fly-hoop-spawn');
    
    // Spawn animation
    void flyHoopEl.offsetWidth;
    flyHoopEl.classList.add('bb-fly-hoop-spawn');
    
    console.log('🎯 Hoop spawned at', Math.round(currentHoop.x), Math.round(currentHoop.y));
  }
  
  function collectHoop(collectorId, collectorName, collectorSprite, collectorColor) {
    if (!currentHoop || currentHoop.collected) return;
    
    currentHoop.collected = true;
    
    // Show collected animation
    if (flyHoopEl) {
      flyHoopEl.classList.add('bb-fly-hoop-collected');
      
      // Show scorer name
      var scorerLabel = document.createElement('div');
      scorerLabel.className = 'bb-fly-hoop-scorer';
      scorerLabel.innerHTML = collectorSprite + ' ' + collectorName + ' +1!';
      scorerLabel.style.color = collectorColor;
      flyHoopEl.appendChild(scorerLabel);
      
      setTimeout(function() {
        flyHoopEl.style.display = 'none';
        flyHoopEl.classList.remove('bb-fly-hoop-collected', 'bb-fly-hoop-spawn');
        var scorer = flyHoopEl.querySelector('.bb-fly-hoop-scorer');
        if (scorer) scorer.remove();
      }, 1000);
    }
    
    // Schedule next hoop
    scheduleNextHoop();
  }
  
  function scheduleNextHoop() {
    if (hoopSpawnTimeout) clearTimeout(hoopSpawnTimeout);
    
    // Only the "host" (lowest connectionId among flying players) spawns hoops
    // This prevents multiple hoops spawning at once
    var amIHoopHost = true;
    if (room) {
      var myId = room.getSelf().connectionId;
      others.forEach(function(user) {
        if (user.presence && user.presence.isFlying && user.connectionId < myId) {
          amIHoopHost = false;
        }
      });
    }
    
    if (!amIHoopHost) {
      console.log('🎯 Not hoop host, waiting for spawn from another player');
      return;
    }
    
    var delay = HOOP_SPAWN_MIN + Math.random() * (HOOP_SPAWN_MAX - HOOP_SPAWN_MIN);
    console.log('🎯 Next hoop in', Math.round(delay / 1000), 'seconds');
    
    hoopSpawnTimeout = setTimeout(function() {
      if (flyModeActive && flyGameActive) {
        spawnHoop(false, null);
      }
    }, delay);
  }
  
  function checkHoopCollision() {
    if (!currentHoop || currentHoop.collected || !flyModeActive) return;
    
    // Distance from avatar center to hoop center
    var dx = flyPosition.x - currentHoop.x;
    var dy = flyPosition.y - currentHoop.y;
    var dist = Math.sqrt(dx * dx + dy * dy);
    
    // Check if within hoop radius (be generous)
    var collisionRadius = HOOP_SIZE / 2 + flyAvatarSize / 4;
    
    if (dist < collisionRadius) {
      // SCORE!
      myFlyScore++;
      console.log('🎯 SCORED! Total:', myFlyScore);
      
      // Play sound
      if (window.BB && window.BB.Sound && window.BB.Sound.play) {
        window.BB.Sound.play('tier-unlock');
      } else if (window.SoundSystem && window.SoundSystem.play) {
        window.SoundSystem.play('tier-unlock');
      }
      
      // Update presence
      if (room) {
        room.updatePresence({ flyScore: myFlyScore });
        
        // Broadcast collection
        var myName = Player ? Player.getName() : 'Player';
        var mySprite = Player ? Player.getTierSprite() : { emoji: '🎴' };
        var myColor = Player ? Player.getColor() : { bg: '#fe5f55' };
        
        room.broadcastEvent({
          type: 'HOOP_COLLECTED',
          collectorId: room.getSelf().connectionId,
          collectorName: myName,
          collectorSprite: mySprite.emoji,
          collectorColor: myColor.bg,
          hoopId: currentHoop.id
        });
      }
      
      collectHoop(
        room ? room.getSelf().connectionId : 'local',
        Player ? Player.getName() : 'Player',
        Player ? Player.getTierSprite().emoji : '🎴',
        Player ? Player.getColor().bg : '#fe5f55'
      );
      
      // Update leaderboard
      updateFlyLeaderboard();
    }
  }
  
  // ========================================================================
  // FLY GAME - Player Collision/Shunting
  // ========================================================================
  
  function checkPlayerCollisions() {
    if (!flyModeActive || !room) return;
    
    var now = Date.now();
    var myId = room.getSelf().connectionId;
    
    others.forEach(function(user) {
      if (!user.presence || !user.presence.isFlying) return;
      
      var peerId = user.connectionId;
      var peer = peerData.get(peerId);
      if (!peer) return;
      
      // Check cooldown
      if (lastCollisionTime[peerId] && now - lastCollisionTime[peerId] < COLLISION_COOLDOWN) {
        return;
      }
      
      // Get peer position in pixels
      var container = document.querySelector('.bundle-builder') || document.body;
      var containerW = container.offsetWidth;
      var containerH = container.offsetHeight;
      
      var peerX = (peer.currentFlyX || peer.flyX || 0) * containerW;
      var peerY = (peer.currentFlyY || peer.flyY || 0) * containerH;
      
      // Distance check
      var dx = flyPosition.x - peerX;
      var dy = flyPosition.y - peerY;
      var dist = Math.sqrt(dx * dx + dy * dy);
      
      // Bigger hit area for more fun collisions!
      var collisionDist = flyAvatarSize * COLLISION_RADIUS_MULTIPLIER;
      
      if (dist < collisionDist && dist > 0) {
        // COLLISION! Shunt both players apart
        lastCollisionTime[peerId] = now;
        
        // Normalize direction
        var nx = dx / dist;
        var ny = dy / dist;
        
        // Apply shunt force to local player (push away from peer)
        flyVelocity.x += nx * SHUNT_FORCE;
        flyVelocity.y += ny * SHUNT_FORCE;
        
        // Visual feedback
        if (myProgressAvatar) {
          myProgressAvatar.classList.add('bb-fly-shunt');
          setTimeout(function() {
            if (myProgressAvatar) myProgressAvatar.classList.remove('bb-fly-shunt');
          }, 200);
        }
        
        // Broadcast collision so peer also gets shunted
        room.broadcastEvent({
          type: 'PLAYER_SHUNT',
          fromId: myId,
          toId: peerId,
          forceX: -nx * SHUNT_FORCE,
          forceY: -ny * SHUNT_FORCE
        });
        
        console.log('💥 COLLISION with', user.presence.name || 'player');
      }
    });
  }
  
  // Listen for Konami Code
  document.addEventListener('keydown', function(e) {
    if (flyModeActive) return; // Don't check code while flying
    
    var expectedKey = konamiSequence[konamiProgress];
    if (e.code === expectedKey) {
      konamiProgress++;
      if (konamiProgress === konamiSequence.length) {
        konamiProgress = 0;
        activateFlyMode();
      }
    } else {
      konamiProgress = 0;
    }
  });
  
  var flyContainer = null; // Store reference to bundle builder container
  var flyOriginalParent = null; // Store original parent to restore later
  
  function activateFlyMode() {
    if (flyModeActive) return;
    if (!myProgressAvatar) {
      console.log('🚀 Cannot activate fly mode - avatar not found');
      return;
    }
    
    // Find the bundle builder container (stays within its stacking context)
    flyContainer = document.querySelector('.bundle-builder') || document.querySelector('#bundle-builder') || document.body;
    flyOriginalParent = myProgressAvatar.parentElement;
    
    flyModeActive = true;
    flyGameActive = true;
    console.log('🚀 KONAMI CODE ACTIVATED! Flying mode enabled!');
    console.log('🏆 FLY GAME STARTED! Race to the hoops!');
    
    // Track konami code discovery
    trackKonamiDiscovery();
    
    // Update presence to show we're in fly game mode
    if (room) {
      room.updatePresence({
        isFlying: true,
        flyGameActive: true,
        flyScore: myFlyScore
      });
    }
    
    // Create and show leaderboard
    createFlyLeaderboard();
    
    // Create hoop element
    createHoopElement();
    
    // Start spawning hoops after a short delay
    setTimeout(function() {
      if (flyModeActive && flyGameActive) {
        spawnHoop(false, null);
      }
    }, 2000);
    
    // Play a fun sound if available
    if (window.BB && window.BB.Sound && window.BB.Sound.play) {
      window.BB.Sound.play('tier-unlock');
    } else if (window.SoundSystem && window.SoundSystem.play) {
      window.SoundSystem.play('tier-unlock');
    }
    
    // Get positions relative to the container
    var containerRect = flyContainer.getBoundingClientRect();
    var avatarRect = myProgressAvatar.getBoundingClientRect();
    
    // Calculate local coordinates within the container
    // We add width/2 and height/2 because our position tracking is center-based
    flyPosition.x = (avatarRect.left - containerRect.left) + (avatarRect.width / 2);
    flyPosition.y = (avatarRect.top - containerRect.top) + (avatarRect.height / 2);
    
    flyVelocity.x = 0;
    flyVelocity.y = 0;
    
    // Move avatar to the bundle builder container for proper z-index stacking
    flyContainer.appendChild(myProgressAvatar);
    
    // Add fly mode class and styles
    myProgressAvatar.classList.add('bb-fly-mode');
    
    // Ensure particle pool is initialized (shared with peer particles)
    ensureParticlePool();
    
    // Key listeners for flying
    document.addEventListener('keydown', handleFlyKeyDown);
    document.addEventListener('keyup', handleFlyKeyUp);
    
    // Start our own animation loop (separate from render loop)
    flyAnimationId = requestAnimationFrame(flyAnimationLoop);
    
    // Flash effect (within the bundle builder container)
    var flash = document.createElement('div');
    flash.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:white;z-index:9000;pointer-events:none;animation:flyFlash 0.3s ease-out forwards;';
    flyContainer.appendChild(flash);
    
    var flashKeyframes = document.createElement('style');
    flashKeyframes.textContent = '@keyframes flyFlash { from { opacity: 0.8; } to { opacity: 0; } }';
    document.head.appendChild(flashKeyframes);
    
    setTimeout(function() {
      flash.remove();
      flashKeyframes.remove();
    }, 300);
    
    // Immediately position avatar at exact starting location
    // Use tier-based scale for avatar size (base 24px * scale, minimum 40px)
    var spriteEl = myProgressAvatar.querySelector('.bb-mp-avatar-sprite');
    var currentScale = spriteEl ? parseFloat(spriteEl.style.getPropertyValue('--avatar-scale')) || 1 : 1;
    flyAvatarSize = Math.max(40, 24 * currentScale); // Cache for animation loop
    console.log('🚀 Fly mode avatar size:', flyAvatarSize, '(scale:', currentScale + ')');
    myProgressAvatar.style.transform = 'translate3d(' + (flyPosition.x - flyAvatarSize/2) + 'px, ' + (flyPosition.y - flyAvatarSize/2) + 'px, 0)';
  }
  
  function handleFlyKeyDown(e) {
    if (!flyModeActive) return;
    
    switch(e.code) {
      case 'ArrowUp':
      case 'KeyW':
        flyKeys.up = true;
        e.preventDefault();
        break;
      case 'ArrowDown':
      case 'KeyS':
        flyKeys.down = true;
        e.preventDefault();
        break;
      case 'ArrowLeft':
      case 'KeyA':
        flyKeys.left = true;
        e.preventDefault();
        break;
      case 'ArrowRight':
      case 'KeyD':
        flyKeys.right = true;
        e.preventDefault();
        break;
      case 'Escape':
        deactivateFlyMode();
        break;
    }
  }
  
  function handleFlyKeyUp(e) {
    if (!flyModeActive) return;
    
    switch(e.code) {
      case 'ArrowUp':
      case 'KeyW':
        flyKeys.up = false;
        break;
      case 'ArrowDown':
      case 'KeyS':
        flyKeys.down = false;
        break;
      case 'ArrowLeft':
      case 'KeyA':
        flyKeys.left = false;
        break;
      case 'ArrowRight':
      case 'KeyD':
        flyKeys.right = false;
        break;
    }
  }
  
  function flyAnimationLoop() {
    if (!flyModeActive || !myProgressAvatar) return;
    
    // Apply gravity (downward force)
    flyVelocity.y += FLY_CONFIG.gravity;
    
    // Apply acceleration based on keys
    if (flyKeys.up) flyVelocity.y -= FLY_CONFIG.acceleration;
    if (flyKeys.down) flyVelocity.y += FLY_CONFIG.acceleration;
    if (flyKeys.left) flyVelocity.x -= FLY_CONFIG.acceleration;
    if (flyKeys.right) flyVelocity.x += FLY_CONFIG.acceleration;
    
    // Apply friction (drift)
    flyVelocity.x *= FLY_CONFIG.friction;
    flyVelocity.y *= FLY_CONFIG.friction;
    
    // Clamp to max speed
    var speed = Math.sqrt(flyVelocity.x * flyVelocity.x + flyVelocity.y * flyVelocity.y);
    if (speed > FLY_CONFIG.maxSpeed) {
      flyVelocity.x = (flyVelocity.x / speed) * FLY_CONFIG.maxSpeed;
      flyVelocity.y = (flyVelocity.y / speed) * FLY_CONFIG.maxSpeed;
    }
    
    // Update position
    flyPosition.x += flyVelocity.x;
    flyPosition.y += flyVelocity.y;
    
    // Boundary collision with bounce - use container dimensions
    var containerW = flyContainer ? flyContainer.offsetWidth : window.innerWidth;
    var containerH = flyContainer ? flyContainer.offsetHeight : window.innerHeight;
    
    // Use cached tier-based avatar size
    var minX = flyAvatarSize / 2;
    var maxX = containerW - flyAvatarSize / 2;
    var minY = flyAvatarSize / 2;
    
    // Calculate floor (maxY) based on avatarLayer (progress bar)
    var maxY = containerH - flyAvatarSize / 2;
    if (avatarLayer && flyContainer) {
      var layerRect = avatarLayer.getBoundingClientRect();
      var containerRect = flyContainer.getBoundingClientRect();
      // The layer is where the avatar sits. So maxY should be its top relative to container
      // plus some offset to sit "on" it.
      var floorY = (layerRect.top - containerRect.top) + 35; // Tweaked offset for visual fit
      if (floorY > 0 && floorY < containerH) {
        maxY = floorY;
      }
    }
    
    if (flyPosition.x < minX) {
      flyPosition.x = minX;
      flyVelocity.x *= -FLY_CONFIG.bounceElasticity;
    } else if (flyPosition.x > maxX) {
      flyPosition.x = maxX;
      flyVelocity.x *= -FLY_CONFIG.bounceElasticity;
    }
    
    if (flyPosition.y < minY) {
      flyPosition.y = minY;
      flyVelocity.y *= -FLY_CONFIG.bounceElasticity;
    } else if (flyPosition.y > maxY) {
      flyPosition.y = maxY;
      flyVelocity.y *= -FLY_CONFIG.bounceElasticity;
    }
    
    // Apply visual tilt based on horizontal velocity
    myProgressAvatar.classList.remove('tilting-left', 'tilting-right');
    if (flyVelocity.x < -2) {
      myProgressAvatar.classList.add('tilting-left');
    } else if (flyVelocity.x > 2) {
      myProgressAvatar.classList.add('tilting-right');
    }
    
    // Update avatar position (GPU accelerated)
    myProgressAvatar.style.transform = 'translate3d(' + (flyPosition.x - flyAvatarSize/2) + 'px, ' + (flyPosition.y - flyAvatarSize/2) + 'px, 0)';
    
    // Spawn particles (rate based on speed)
    // Only spawn if moving fast enough
    if (speed > 1) {
      var spawnCount = Math.floor(speed * 0.2) + 1;
      for (var i = 0; i < spawnCount; i++) {
        if (Math.random() < 0.5) spawnFlyParticle();
      }
    }
    
    // Update existing particles
    updateFlyParticles();
    
    // Check for hoop collision (FLY GAME)
    checkHoopCollision();
    
    // Check for player collisions (FLY GAME)
    checkPlayerCollisions();
    
    // Broadcast position (throttled)
    var now = Date.now();
    if (now - lastBroadcastTime > BROADCAST_THROTTLE) {
      lastBroadcastTime = now;
      if (room) {
        room.updatePresence({
          isFlying: true,
          flyX: flyPosition.x / containerW,
          flyY: flyPosition.y / containerH,
          flyGameActive: flyGameActive,
          flyScore: myFlyScore
        });
      }
    }
    
    // Continue loop
    flyAnimationId = requestAnimationFrame(flyAnimationLoop);
  }
  
  function spawnFlyParticle() {
    // Use my random color palette for local particles (isLocal = true for longer trails)
    var myPalette = PARTICLE_COLOR_PALETTES[myParticleColorIndex];
    var myColor = myPalette[Math.floor(Math.random() * myPalette.length)];
    spawnParticleAt(flyPosition.x, flyPosition.y, flyVelocity.x, flyVelocity.y, myColor, true);
  }
  
  function updateFlyParticles() {
    particlePool.forEach(function(p) {
      if (!p.active) return;
      
      p.life--;
      if (p.life <= 0) {
        p.active = false;
        p.el.style.display = 'none';
        return;
      }
      
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.05; // Light gravity on particles
      
      var scale = p.life / p.maxLife;
      p.el.style.transform = 'translate3d(' + p.x + 'px, ' + p.y + 'px, 0) scale(' + scale + ')';
      p.el.style.opacity = scale;
    });
  }
  
  function deactivateFlyMode() {
    if (!flyModeActive) return;
    flyModeActive = false;
    flyGameActive = false;
    
    // Broadcast stop
    if (room) {
      room.updatePresence({ 
        isFlying: false,
        flyGameActive: false
      });
    }
    
    console.log('🛬 Flying mode deactivated');
    console.log('🏆 Final score:', myFlyScore);
    
    // Cancel animation
    if (flyAnimationId) {
      cancelAnimationFrame(flyAnimationId);
      flyAnimationId = null;
    }
    
    // Cancel hoop spawning
    if (hoopSpawnTimeout) {
      clearTimeout(hoopSpawnTimeout);
      hoopSpawnTimeout = null;
    }
    
    // Remove key listeners
    document.removeEventListener('keydown', handleFlyKeyDown);
    document.removeEventListener('keyup', handleFlyKeyUp);
    
    // Move avatar back to avatar layer and reset
    if (myProgressAvatar && avatarLayer) {
      myProgressAvatar.classList.remove('bb-fly-mode', 'tilting-left', 'tilting-right', 'bb-fly-shunt');
      avatarLayer.appendChild(myProgressAvatar);
      // Reset to current progress position
      // Cap max position to account for avatar width (50px)
      var maxX = avatarBarWidth - 50;
      var xPos = myCurrentAvatarX * maxX;
      myProgressAvatar.style.transform = 'translate3d(' + xPos + 'px, 0, 0)';
    }
    
    // Reset particles
    particlePool.forEach(function(p) {
      p.active = false;
      p.el.style.display = 'none';
    });
    
    // Hide hoop
    if (flyHoopEl) {
      flyHoopEl.style.display = 'none';
    }
    currentHoop = null;
    
    // Remove leaderboard with animation
    removeFlyLeaderboard();
    
    // Reset keys
    flyKeys = { up: false, down: false, left: false, right: false };
    lastCollisionTime = {};
  }
  
  // Expose fly mode state so render loop knows to skip normal updates
  function isFlyModeActive() {
    return flyModeActive;
  }
  
  // ========================================================================
  // ANALYTICS TRACKING
  // ========================================================================
  
  var sessionStartTime = null;
  var hasTrackedKonami = false;
  var hasTrackedVisitor = false;
  
  // Track unique visitor (once per day per device)
  function trackVisitor() {
    if (hasTrackedVisitor) return;
    
    // Check if already tracked today
    var today = new Date().toISOString().split('T')[0]; // "2024-12-09"
    var lastTrackedDate = localStorage.getItem('bb_visitor_tracked_date');
    
    if (lastTrackedDate === today) {
      console.log('📊 [Analytics] Visitor already tracked today');
      hasTrackedVisitor = true;
      return;
    }
    
    hasTrackedVisitor = true;
    
    var playerData = Player.getPlayerData ? Player.getPlayerData() : {};
    var odId = playerData.odId || 'anonymous';
    
    console.log('📊 [Analytics] Tracking visitor:', odId);
    
    fetch(FIREBASE_FUNCTIONS_URL + '/trackVisitor', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        odId: odId,
        date: today,
        userAgent: navigator.userAgent,
        referrer: document.referrer || 'direct'
      })
    }).then(function(res) {
      if (!res.ok) {
        throw new Error('HTTP ' + res.status);
      }
      return res.json();
    }).then(function(data) {
      // Mark as tracked for today
      localStorage.setItem('bb_visitor_tracked_date', today);
      console.log('📊 [Analytics] Visitor tracked! Today:', data.todayCount || '?', 'All-time:', data.totalCount || '?');
    }).catch(function(err) {
      console.warn('📊 [Analytics] Failed to track visitor:', err);
      // Don't save to localStorage on failure - try again on next page load
      hasTrackedVisitor = false;
    });
  }
  
  // Track konami code discovery
  function trackKonamiDiscovery() {
    if (hasTrackedKonami) return;
    hasTrackedKonami = true;
    
    var playerData = Player.getPlayerData ? Player.getPlayerData() : {};
    var odId = playerData.odId || 'anonymous';
    
    console.log('🎮 Tracking Konami discovery for:', odId);
    
    fetch(FIREBASE_FUNCTIONS_URL + '/trackKonamiCode', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        odId: odId,
        playerName: playerData.name || 'Anonymous',
        team: playerData.team || 'unknown'
      })
    }).then(function(res) {
      if (!res.ok) {
        throw new Error('HTTP ' + res.status);
      }
      return res.json();
    }).then(function(data) {
      if (data.newDiscovery) {
        console.log('🎮 NEW Konami code discovery tracked! Total discoveries:', data.totalDiscoveries || '?');
      } else {
        console.log('🎮 Konami code already discovered by this user (odId:', odId, ')');
      }
    }).catch(function(err) {
      console.warn('❌ Failed to track konami code:', err);
    });
  }
  
  // Track session end (called on page unload)
  function trackSessionEnd() {
    if (!sessionStartTime) return;
    
    var duration = Date.now() - sessionStartTime;
    if (duration < 1000) return; // Ignore < 1 second sessions
    
    var playerData = Player.getPlayerData ? Player.getPlayerData() : {};
    var reachedCheckout = myStatus === 'playing';
    
    // Use sendBeacon for reliable tracking on page unload
    var data = JSON.stringify({
      odId: playerData.odId || 'anonymous',
      durationMs: duration,
      reachedCheckout: reachedCheckout
    });
    
    if (navigator.sendBeacon) {
      navigator.sendBeacon(FIREBASE_FUNCTIONS_URL + '/trackSessionEnd', data);
    } else {
      // Fallback for older browsers
      fetch(FIREBASE_FUNCTIONS_URL + '/trackSessionEnd', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: data,
        keepalive: true
      });
    }
  }
  
  // Start session tracking
  function startSessionTracking() {
    sessionStartTime = Date.now();
    
    // Track session end on page unload
    window.addEventListener('beforeunload', trackSessionEnd);
    window.addEventListener('pagehide', trackSessionEnd);
  }
  
  // ========================================================================
  // PUBLIC API
  // ========================================================================
  
  return {
    version: VERSION,
    init: init,
    enterGame: enterGame,
    flyOff: flyOff,
    updatePlayerCustomization: updatePlayerCustomization,
    updateAvatarPosition: updateAvatarPosition,
    renderAvatars: renderAvatars,
    getOthersCount: getOthersCount,
    jumpMyAvatar: jumpMyAvatar,
    evolveMyAvatar: evolveMyAvatar,
    
    // Queue system
    onRelease: onRelease,
    onQueueUpdate: onQueueUpdate,
    getGlobalQueueCount: getGlobalQueueCount,
    getBatchNumber: getBatchNumber,
    getIsReleased: getIsReleased,
    forceRelease: forceRelease,  // DEV: Bypass queue for testing
    
    // Easter egg
    activateFlyMode: activateFlyMode,
    deactivateFlyMode: deactivateFlyMode,
    isFlyModeActive: isFlyModeActive,
    
    // Performance
    setPeersVisible: setPeersVisible
  };
})(window.BB.Player);

// Backwards compatibility alias
window.MultiplayerSystem = window.BB.Multiplayer;


